import { useState, useRef, useCallback, useEffect, useMemo } from "react";
import { useTranslation, Trans } from "react-i18next";
import type { TFunction } from "i18next";
import { fmtDate, intlLocale } from "@/lib/locale";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Input } from "@/components/ui/input";
import { useToast } from "@/hooks/use-toast";
import { uploadFileDirect } from "@/lib/directUpload";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip,
  ResponsiveContainer, AreaChart, Area, ReferenceLine, Legend,
  BarChart, Bar, Cell,
} from "recharts";
import {
  FileText, Trash2, RefreshCw, TrendingUp, TrendingDown,
  DollarSign, Activity, AlertTriangle, ChevronDown, ChevronRight,
  Plus, Info, Download, Eye, Pencil, CheckCircle2, X,
} from "lucide-react";
import {
  deriveBankStatementUiReadiness,
  deriveReadinessInputFromStatement,
  type BankStatementUiReadiness,
} from "@/lib/bankStatementUiReadiness";
import {
  computeBankHealthHeadlineMetrics,
  effectiveBankCredits,
  effectiveBankMoneyOut,
} from "@/lib/bankHealthMetrics";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { Chart, BarController, BarElement, CategoryScale, LinearScale, Tooltip as ChartTooltip } from "chart.js";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { apiRequest } from "@/lib/queryClient";
import {
  loadBankHealthDateFilter,
  persistBankHealthDateFilter,
  normalizeCustomBounds,
  isCustomRangeInvalid as computeIsCustomRangeInvalid,
  computeAvailableYears,
  getBankStatementMonthKey,
  type DateRangeOption,
  type CustomMode,
} from "@/lib/bankHealthDateFilter";

Chart.register(BarController, BarElement, CategoryScale, LinearScale, ChartTooltip);

const DATE_RANGE_VALUES: DateRangeOption[] = ["3mo", "qtr", "6mo", "ytd", "all", "custom"];

function formatMonthYear(ym: string): string {
  const parts = ym.split("-");
  if (parts.length < 2) return ym;
  const d = new Date(parseInt(parts[0], 10), parseInt(parts[1], 10) - 1, 1);
  if (isNaN(d.getTime())) return ym;
  return fmtDate(d, { month: "short", year: "numeric" });
}

function monthOptions(): { value: string; label: string }[] {
  return Array.from({ length: 12 }, (_, i) => ({
    value: String(i + 1).padStart(2, "0"),
    label: new Date(2000, i, 1).toLocaleDateString(intlLocale(), { month: "long" }),
  }));
}

// Friendly Month + Year dropdown pair that edits a "YYYY-MM" value, replacing
// the native <input type="month"> calendar popup which was hard to use.
function MonthYearSelect({
  value,
  onChange,
  years,
  invalid,
  testid,
}: {
  value: string;
  onChange: (ym: string) => void;
  years: string[];
  invalid?: boolean;
  testid: string;
}) {
  const [yearPart, monthPart] = (() => {
    const parts = value.split("-");
    const now = new Date();
    return [parts[0] || String(now.getFullYear()), parts[1] || "01"];
  })();
  const triggerCls = `h-7 text-xs ${invalid ? "border-destructive focus:ring-destructive" : ""}`;
  return (
    <div className="flex items-center gap-1.5">
      <Select value={monthPart} onValueChange={(m) => onChange(`${yearPart}-${m}`)}>
        <SelectTrigger className={`${triggerCls} w-[120px]`} data-testid={`${testid}-month`}>
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {monthOptions().map((mo) => (
            <SelectItem key={mo.value} value={mo.value} className="text-xs">
              {mo.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      <Select value={yearPart} onValueChange={(y) => onChange(`${y}-${monthPart}`)}>
        <SelectTrigger className={`${triggerCls} w-[88px]`} data-testid={`${testid}-year`}>
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {years.map((y) => (
            <SelectItem key={y} value={y} className="text-xs">
              {y}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}

function getDateRangeCutoff(range: Exclude<DateRangeOption, "custom" | "all">): string {
  const now = new Date();
  let cutoff: Date;
  if (range === "3mo") {
    cutoff = new Date(now.getFullYear(), now.getMonth() - 3, 1);
  } else if (range === "qtr") {
    const qtrStart = Math.floor(now.getMonth() / 3) * 3;
    cutoff = new Date(now.getFullYear(), qtrStart, 1);
  } else if (range === "6mo") {
    cutoff = new Date(now.getFullYear(), now.getMonth() - 6, 1);
  } else {
    cutoff = new Date(now.getFullYear(), 0, 1);
  }
  const y = cutoff.getFullYear();
  const m = String(cutoff.getMonth() + 1).padStart(2, "0");
  return `${y}-${m}`;
}

function formatDateSpan(range: Exclude<DateRangeOption, "custom">, t: TFunction): string {
  if (range === "all") return t("bank:dateRange.allUploaded");
  const now = new Date();
  let startDate: Date;
  if (range === "3mo") {
    startDate = new Date(now.getFullYear(), now.getMonth() - 3, 1);
  } else if (range === "qtr") {
    const qtrStart = Math.floor(now.getMonth() / 3) * 3;
    startDate = new Date(now.getFullYear(), qtrStart, 1);
  } else if (range === "6mo") {
    startDate = new Date(now.getFullYear(), now.getMonth() - 6, 1);
  } else {
    startDate = new Date(now.getFullYear(), 0, 1);
  }
  const endDate = new Date(now.getFullYear(), now.getMonth(), 1);
  const startStr = fmtDate(startDate, { month: "short", year: "numeric" });
  const endStr = fmtDate(endDate, { month: "short", year: "numeric" });
  return `${startStr} – ${endStr}`;
}

type BankStatement = {
  id: string;
  fileName: string;
  filePath: string | null;
  fileType: string;
  accountPeriodStart: string | null;
  accountPeriodEnd: string | null;
  openingBalance: string | null;
  closingBalance: string | null;
  totalCredits: string | null;
  totalDebits: string | null;
  statedCredits: string | null;
  statedDebits: string | null;
  serviceFees: string | null;
  checksAmount: string | null;
  moneyOutOverride: string | null;
  avgDailyBalance: string | null;
  avgDailyBalanceSource: string | null;
  avgDailyBalanceVerified: boolean | null;
  avgDailyBalanceAuditJson: string | null;
  notableItemsJson: string | null;
  confidenceJson: string | null;
  accuracyScore: string | null;
  warningsJson: string | null;
  needsReview: boolean;
  reviewMessage: string | null;
  transactionExtractionFailed: boolean;
  transactionExtractionError: string | null;
  fallbackPath: string | null;
  bankReadinessJson: string | null;
  dealerCorrectedCredits: string | null;
  dealerCorrectedDebits: string | null;
  dealerCorrectedAvgBalance: string | null;
  dealerVerifiedAt: string | null;
  createdAt: string;
};

type BankTransaction = {
  id: string;
  bankStatementId: string | null;
  transactionDate: string | null;
  payee: string | null;
  description: string | null;
  amount: string | null;
  type: string | null;
  category: string | null;
  confidence: string | null;
  manuallyCorrected: boolean;
  postingStatus: string | null;
  includedInStatementTotals: boolean | null;
  exclusionReason: string | null;
  linkedTransactionId: string | null;
  normalizationEligibleForTotals: boolean | null;
};

function isUsableBankTransaction(
  transaction: BankTransaction,
): transaction is BankTransaction & { amount: string; type: "credit" | "debit" } {
  const amount = parseN(transaction.amount);
  return (
    transaction.normalizationEligibleForTotals !== false &&
    (transaction.type === "credit" || transaction.type === "debit") &&
    amount !== null &&
    Number.isFinite(amount)
  );
}


function dateRangeOptions(t: TFunction): { value: DateRangeOption; label: string; pdfLabel: string }[] {
  return DATE_RANGE_VALUES.map((value) => ({
    value,
    label: t(`bank:dateRange.${value}`),
    pdfLabel: t(`bank:dateRange.pdf.${value}`),
  }));
}

function analysisStepsBank(t: TFunction): string[] {
  return [
    t("bank:analysisSteps.readingStructure"),
    t("bank:analysisSteps.identifyingPeriod"),
    t("bank:analysisSteps.extractingOpening"),
    t("bank:analysisSteps.extractingClosing"),
    t("bank:analysisSteps.calculatingDeposits"),
    t("bank:analysisSteps.calculatingWithdrawals"),
    t("bank:analysisSteps.identifyingNotable"),
    t("bank:analysisSteps.scoringConfidence"),
    t("bank:analysisSteps.parsingRows"),
    t("bank:analysisSteps.identifyingPayees"),
    t("bank:analysisSteps.applyingRules"),
    t("bank:analysisSteps.categorizing"),
    t("bank:analysisSteps.scoringCategorization"),
    t("bank:analysisSteps.finalizing"),
  ];
}

function fmtMonth(ym: string): string {
  const [y, m] = ym.split("-");
  const d = new Date(parseInt(y, 10), parseInt(m, 10) - 1, 1);
  if (isNaN(d.getTime())) return `${m} ${y}`;
  return fmtDate(d, { month: "short", year: "numeric" });
}

function fmt$(n: number | null | undefined, decimals = 0): string {
  if (n == null || isNaN(n)) return "—";
  return n.toLocaleString("en-US", { style: "currency", currency: "USD", maximumFractionDigits: decimals });
}

function fmtCompact(n: number): string {
  if (Math.abs(n) >= 1_000_000) return `$${(n / 1_000_000).toFixed(1)}M`;
  if (Math.abs(n) >= 1_000) return `$${(n / 1_000).toFixed(0)}k`;
  return `$${n.toFixed(0)}`;
}

function parseN(v: string | null | undefined): number | null {
  if (!v) return null;
  const n = parseFloat(v);
  return isFinite(n) ? n : null;
}

// Honest total money-out from a statement's stated summary figures. Some banks
// (e.g. BofA) report "Checks" and "Service fees" as separate summary lines
// OUTSIDE the withdrawals/debits subtotal, so totalDebits alone understates the
// real spend. For banks that fold checks/fees into totalDebits, checksAmount and
// serviceFees are null, so this never double-counts.
//
// moneyOutOverride is the precomputed headline for a uniquely-resolved unknown-bank
// FOLDED statement, where the printed Checks subtotal is folded INTO totalDebits and
// the additive formula below would double-count it. When present, use it verbatim —
// the presentation layer never re-derives folding (it is set server-side only for
// the folded case; separate/ambiguous/known/multi-account statements leave it null).
const effectiveCredits = effectiveBankCredits;
const effectiveMoneyOut = effectiveBankMoneyOut;
function effectiveAvgBalance(s: BankStatement): number | null {
  if (s.dealerVerifiedAt) return parseN(s.dealerCorrectedAvgBalance);
  return parseN(s.avgDailyBalance);
}

// Provenance of the displayed average balance, so the UI can show whether the
// number is trustworthy or just the model's unverified estimate. A verified
// average is either dealer-confirmed or computed from the bank's printed Daily
// Balance Summary / per-row running balances; anything else is an estimate.
function avgBalanceProvenance(s: BankStatement, t: TFunction): { label: string; verified: boolean } | null {
  if (effectiveAvgBalance(s) == null) return null;
  if (s.dealerVerifiedAt) return { label: t("bank:avgProvenance.dealerConfirmed"), verified: true };
  // "verified" is keyed off the persisted audit flag — a stored ADB only counts
  // as verified when the exact source rows + the full computation were retained.
  // The source label is descriptive only; it never upgrades an unverified value.
  if (!s.avgDailyBalanceVerified) {
    return { label: t("bank:avgProvenance.estimatedNotVerified"), verified: false };
  }
  switch (s.avgDailyBalanceSource) {
    case "daily_balance_summary_table":
      return { label: t("bank:avgProvenance.fromDailyBalanceSummary"), verified: true };
    case "running_balances":
      return { label: t("bank:avgProvenance.fromRunningBalances"), verified: true };
    case "stated_on_statement":
      // The bank printed this average itself — trusted, but distinct from a value
      // we recomputed line-by-line. Shown calmly as coming straight from the page.
      return { label: t("bank:avgProvenance.fromStatement"), verified: true };
    default:
      return { label: t("bank:avgProvenance.verified"), verified: true };
  }
}

// Maps an internal English avg-daily-balance source string to a localized label.
function avgSourceLabel(src: string, t: TFunction): string {
  switch (src) {
    case "Dealer-verified": return t("bank:avgSource.dealerVerified");
    case "Estimated — not verified": return t("bank:avgSource.estimatedNotVerified");
    case "Daily Balance Summary": return t("bank:avgSource.dailyBalanceSummary");
    case "Running balances": return t("bank:avgSource.runningBalances");
    case "From statement": return t("bank:avgSource.fromStatement");
    case "Verified": return t("bank:avgSource.verified");
    case "Calculated": return t("bank:avgSource.calculated");
    case "Combined": return t("bank:avgSource.combined");
    default: return src;
  }
}

// Localized label for a transaction category (server stores English enum values).
function categoryLabel(cat: string | null | undefined, t: TFunction): string {
  switch (cat) {
    case "Floorplan Payments": return t("bank:categories.floorplanPayments");
    case "Auction / Inventory": return t("bank:categories.auctionInventory");
    case "Parts / Recon": return t("bank:categories.partsRecon");
    case "Payroll": return t("bank:categories.payroll");
    case "Insurance": return t("bank:categories.insurance");
    case "Utilities": return t("bank:categories.utilities");
    case "Fuel": return t("bank:categories.fuel");
    case "Marketing": return t("bank:categories.marketing");
    case "Software / Subscriptions": return t("bank:categories.softwareSubscriptions");
    case "Bank Fees": return t("bank:categories.bankFees");
    case "DMV / Title": return t("bank:categories.dmvTitle");
    case "Rent / Property": return t("bank:categories.rentProperty");
    case "Meals / Travel": return t("bank:categories.mealsTravel");
    case "Office / Supplies": return t("bank:categories.officeSupplies");
    case "Transfers / Owner Draws": return t("bank:categories.transfersOwnerDraws");
    case "Vendor/Parts": return t("bank:categories.vendorParts");
    case "Unitemized (statement total)": return t("bank:categories.unitemized");
    case "Other":
    default: return t("bank:categories.other");
  }
}

// Formats a raw numeric input string with thousands separators for display,
// preserving an in-progress decimal (e.g. "13545." -> "13,545.") so typing is
// never blocked. The underlying state stays comma-free for parseFloat.
function formatThousands(raw: string): string {
  if (raw === "") return "";
  const neg = raw.trim().startsWith("-");
  const cleaned = raw.replace(/[^0-9.]/g, "");
  if (cleaned === "") return "";
  const dot = cleaned.indexOf(".");
  const intPart = dot === -1 ? cleaned : cleaned.slice(0, dot);
  const decPart = dot === -1 ? "" : cleaned.slice(dot + 1).replace(/\./g, "");
  const intFmt = (intPart || "0").replace(/^0+(?=\d)/, "").replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  let out = intFmt;
  if (dot !== -1) out += "." + decPart;
  return (neg ? "-" : "") + out;
}

// Strips formatting (currency symbol, commas) back to a clean numeric string,
// canonicalizing to at most one decimal point so malformed values like "1..2"
// can never reach state or be saved.
function unformatNumber(value: string): string {
  const cleaned = value.replace(/[^0-9.]/g, "");
  const dot = cleaned.indexOf(".");
  if (dot === -1) return cleaned;
  return cleaned.slice(0, dot + 1) + cleaned.slice(dot + 1).replace(/\./g, "");
}

// Currency text input that shows a "$" adornment and live comma grouping while
// keeping the stored value comma-free.
function CurrencyInput({
  value,
  onChange,
  placeholder,
  testId,
}: {
  value: string;
  onChange: (next: string) => void;
  placeholder?: string;
  testId?: string;
}) {
  return (
    <div className="relative">
      <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-sm text-muted-foreground">$</span>
      <Input
        inputMode="decimal"
        className="pl-6"
        value={formatThousands(value)}
        onChange={(e) => onChange(unformatNumber(e.target.value))}
        placeholder={placeholder}
        data-testid={testId}
      />
    </div>
  );
}

// Editable summary correction for a Review Required statement. Dealers enter the
// true headline totals so the statement can feed the summary-level Bank Health
// charts. Defaults are prefilled from the extracted/effective figures.
function ReviewSummaryEditor({
  statement,
  onSave,
  isSaving,
}: {
  statement: BankStatement;
  onSave: (values: { credits: string; debits: string; avgBalance: string }) => void;
  isSaving: boolean;
}) {
  const { t } = useTranslation(["bank", "common"]);
  const initialCredits = effectiveCredits(statement);
  const initialDebits = effectiveMoneyOut(statement);
  const initialAvg = effectiveAvgBalance(statement);
  const [credits, setCredits] = useState(initialCredits != null ? String(initialCredits) : "");
  const [debits, setDebits] = useState(initialDebits != null ? String(initialDebits) : "");
  const [avgBalance, setAvgBalance] = useState(initialAvg != null ? String(initialAvg) : "");
  const creditsNum = parseFloat(credits);
  const debitsNum = parseFloat(debits);
  const avgValid = avgBalance.trim() === "" || Number.isFinite(parseFloat(avgBalance));
  const valid =
    Number.isFinite(creditsNum) && creditsNum >= 0 &&
    Number.isFinite(debitsNum) && debitsNum >= 0 && avgValid;
  return (
    <div className="rounded-md border bg-background p-3 space-y-2" data-testid={`summary-editor-${statement.id}`}>
      <p className="text-xs font-medium truncate">{statement.fileName}</p>
      <p className="text-[11px] text-muted-foreground">
        {t("bank:reviewEditor.instructions")}
      </p>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
        <div className="space-y-1">
          <label className="text-[11px] font-medium text-muted-foreground">{t("bank:reviewEditor.moneyIn")}</label>
          <CurrencyInput value={credits} onChange={setCredits} placeholder={t("bank:reviewEditor.amountPlaceholder")} testId={`input-credits-${statement.id}`} />
        </div>
        <div className="space-y-1">
          <label className="text-[11px] font-medium text-muted-foreground">{t("bank:reviewEditor.moneyOut")}</label>
          <CurrencyInput value={debits} onChange={setDebits} placeholder={t("bank:reviewEditor.amountPlaceholder")} testId={`input-debits-${statement.id}`} />
        </div>
        <div className="space-y-1">
          <label className="text-[11px] font-medium text-muted-foreground">{t("bank:reviewEditor.averageBalance")}</label>
          <CurrencyInput value={avgBalance} onChange={setAvgBalance} placeholder={t("bank:reviewEditor.optional")} testId={`input-avgbalance-${statement.id}`} />
        </div>
      </div>
      <div className="flex justify-end">
        <Button
          size="sm"
          disabled={!valid || isSaving}
          onClick={() => onSave({ credits, debits, avgBalance })}
          data-testid={`button-save-summary-${statement.id}`}
        >
          {isSaving ? t("bank:reviewEditor.saving") : t("bank:reviewEditor.save")}
        </Button>
      </div>
    </div>
  );
}

function confBadge(conf: string | null): { color: string; label: string } {
  const n = parseFloat(conf ?? "0");
  if (n >= 0.85) return { color: "text-emerald-600 bg-emerald-500/10", label: `${Math.round(n * 100)}%` };
  if (n >= 0.60) return { color: "text-yellow-600 bg-yellow-500/10", label: `${Math.round(n * 100)}%` };
  return { color: "text-destructive bg-destructive/10", label: `${Math.round(n * 100)}%` };
}

type KpiHealth = "green" | "yellow" | "red";

function kpiColor(health: KpiHealth): string {
  if (health === "green") return "text-emerald-600 dark:text-emerald-400";
  if (health === "yellow") return "text-amber-600 dark:text-amber-400";
  return "text-rose-500 dark:text-rose-400";
}

function kpiBg(health: KpiHealth): string {
  if (health === "green") return "border-l-4 border-l-emerald-400";
  if (health === "yellow") return "border-l-4 border-l-amber-400";
  return "border-l-4 border-l-rose-400";
}

function KpiCard({
  title, value, health, benchmark, icon: Icon, description,
}: {
  title: string;
  value: string;
  health: KpiHealth;
  benchmark: string;
  icon: any;
  description?: string;
}) {
  const { t } = useTranslation(["bank", "common"]);
  const testId = `kpi-card-${title.toLowerCase().replace(/\s+/g, "-")}`;

  return (
    <Card
      className={`${kpiBg(health)} transition-all`}
      data-testid={testId}
    >
      <CardContent className="p-3">
        <div className="flex items-start justify-between gap-2">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-1 mb-1">
              <p className="text-xs text-muted-foreground font-medium">{title}</p>
              {description && (
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <button
                        type="button"
                        aria-label={t("bank:kpi.aboutLabel", { title })}
                        className="inline-flex items-center justify-center rounded focus:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                        data-testid={`${testId}-info`}
                      >
                        <Info className="w-3 h-3 text-muted-foreground" />
                      </button>
                    </TooltipTrigger>
                    <TooltipContent className="max-w-xs text-xs leading-relaxed" data-testid={`${testId}-tooltip`}>
                      {description}
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              )}
            </div>
            <p className="text-lg font-bold text-foreground">{value}</p>
            <p className="text-xs text-muted-foreground mt-0.5 leading-tight">{benchmark}</p>
          </div>
          <div className="flex flex-col items-end gap-1">
            <div className={`w-7 h-7 rounded-lg flex items-center justify-center shrink-0 ${
              health === "green" ? "bg-emerald-500/10" : health === "yellow" ? "bg-amber-500/10" : "bg-rose-500/10"
            }`}>
              <Icon className={`w-3.5 h-3.5 ${kpiColor(health)}`} />
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function StatementUploadProgress({ steps, error, currentStep }: { steps: string[]; error?: string | null; currentStep?: string | null }) {
  const { t } = useTranslation(["bank", "common"]);
  const [streamLines, setStreamLines] = useState<string[]>([]);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    if (error) {
      if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; }
      return;
    }
    setStreamLines([]);
    let idx = 0;
    timerRef.current = setInterval(() => {
      setStreamLines((prev) => {
        const line = idx < steps.length ? steps[idx] : t("bank:analysisSteps.stillProcessing");
        idx++;
        return [...prev, line];
      });
    }, 900);
    return () => { if (timerRef.current) clearInterval(timerRef.current); };
  }, [steps, error, t]);

  if (error) {
    return (
      <div className="flex items-start gap-2 py-1 text-destructive" data-testid="statement-upload-error">
        <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
        <span className="text-xs font-medium">{error}</span>
      </div>
    );
  }

  // Prefer the ticker once it has cycled at least once; fall back to the real
  // server step (set immediately on mount-resume) so we never show "Starting…"
  // when picking up a job that's already mid-way through extraction.
  const currentLine = streamLines[streamLines.length - 1] ?? currentStep ?? t("bank:analysisSteps.starting");

  return (
    <div className="flex items-center gap-2 py-1" data-testid="statement-upload-progress">
      <RefreshCw className="w-3.5 h-3.5 text-primary animate-spin shrink-0" />
      <span className="text-xs text-primary font-medium truncate">{currentLine}</span>
    </div>
  );
}

const TRANSACTION_CATEGORY_OPTIONS = [
  "Floorplan Payments",
  "Auction / Inventory",
  "Parts / Recon",
  "Payroll",
  "Insurance",
  "Utilities",
  "Fuel",
  "Marketing",
  "Software / Subscriptions",
  "Bank Fees",
  "DMV / Title",
  "Rent / Property",
  "Meals / Travel",
  "Office / Supplies",
  "Transfers / Owner Draws",
  "Vendor/Parts",
  "Other",
] as const;

const CATEGORY_COLORS: Record<string, string> = {
  "Floorplan Payments": "bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300",
  "Auction / Inventory": "bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300",
  "Parts / Recon": "bg-slate-100 dark:bg-slate-700 text-slate-700 dark:text-slate-300",
  "Payroll": "bg-indigo-100 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-300",
  "Insurance": "bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300",
  "Utilities": "bg-cyan-100 dark:bg-cyan-900/30 text-cyan-700 dark:text-cyan-300",
  "Fuel": "bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-300",
  "Marketing": "bg-pink-100 dark:bg-pink-900/30 text-pink-700 dark:text-pink-300",
  "Software / Subscriptions": "bg-violet-100 dark:bg-violet-900/30 text-violet-700 dark:text-violet-300",
  "Bank Fees": "bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300",
  "DMV / Title": "bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-300",
  "Rent / Property": "bg-lime-100 dark:bg-lime-900/30 text-lime-700 dark:text-lime-300",
  "Meals / Travel": "bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300",
  "Office / Supplies": "bg-stone-100 dark:bg-stone-700 text-stone-700 dark:text-stone-300",
  "Transfers / Owner Draws": "bg-teal-100 dark:bg-teal-900/30 text-teal-700 dark:text-teal-300",
  "Vendor/Parts": "bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-400",
  "Other": "bg-muted text-muted-foreground",
};

const CATEGORY_CHART_HEX: Record<string, string> = {
  "Floorplan Payments": "#8b5cf6",
  "Auction / Inventory": "#3b82f6",
  "Parts / Recon": "#64748b",
  "Payroll": "#6366f1",
  "Insurance": "#f59e0b",
  "Utilities": "#06b6d4",
  "Fuel": "#f97316",
  "Marketing": "#ec4899",
  "Software / Subscriptions": "#7c3aed",
  "Bank Fees": "#ef4444",
  "DMV / Title": "#eab308",
  "Rent / Property": "#84cc16",
  "Meals / Travel": "#22c55e",
  "Office / Supplies": "#78716c",
  "Transfers / Owner Draws": "#14b8a6",
  "Vendor/Parts": "#94a3b8",
  "Other": "#9ca3af",
};

function categoryColor(cat: string | null): string {
  return CATEGORY_COLORS[cat ?? "Other"] ?? CATEGORY_COLORS["Other"];
}

export function BankHealthTab() {
  const { t } = useTranslation(["bank", "common"]);
  const { toast } = useToast();
  const qc = useQueryClient();

  // Hydrate the persisted Custom-filter selection once. loadBankHealthDateFilter
  // applies defaults for missing keys and collapses month-mode bounds, so a
  // month-mode selection can never hydrate with stale, mismatched From/To.
  const [dateRange, setDateRange] = useState<DateRangeOption>(() => loadBankHealthDateFilter(localStorage).dateRange);
  const [customFrom, setCustomFrom] = useState<string>(() => loadBankHealthDateFilter(localStorage).customFrom);
  const [customTo, setCustomTo] = useState<string>(() => loadBankHealthDateFilter(localStorage).customTo);
  const [customMode, setCustomMode] = useState<CustomMode>(() => loadBankHealthDateFilter(localStorage).customMode);
  useEffect(() => {
    persistBankHealthDateFilter(localStorage, { dateRange, customFrom, customTo, customMode });
  }, [dateRange, customFrom, customTo, customMode]);

  // "Specific month" mode keeps a single month selection, so From and To always
  // collapse to the same value. Switching INTO month mode collapses To onto From.
  const selectCustomMode = useCallback((mode: CustomMode) => {
    setCustomMode(mode);
    setCustomTo(normalizeCustomBounds(mode, customFrom, customTo).customTo);
  }, [customFrom, customTo]);
  const setSpecificMonth = useCallback((ym: string) => {
    setCustomFrom(ym);
    setCustomTo(ym);
  }, []);
  // Month mode requires customFrom===customTo. Persisted localStorage (or a prior
  // range selection) can hydrate with divergent bounds, which would silently
  // apply stale range filtering with no error shown — collapse them here.
  useEffect(() => {
    const normalized = normalizeCustomBounds(customMode, customFrom, customTo);
    if (normalized.customTo !== customTo) setCustomTo(normalized.customTo);
  }, [customMode, customFrom, customTo]);

  const [showExportConfirm, setShowExportConfirm] = useState(false);
  const [uploadingStatement, setUploadingStatement] = useState(false);
  const [uploadBankError, setUploadBankError] = useState<string | null>(null);
  const [uploadStepLabel, setUploadStepLabel] = useState<string | null>(null);
  const statementFileRef = useRef<HTMLInputElement>(null);
  const uploadCancelledRef = useRef(false);
  const activeUploadJobIdRef = useRef<string | null>(null);
  const [pendingDeleteId, setPendingDeleteId] = useState<string | null>(null);
  const [expandedStatements, setExpandedStatements] = useState(true);
  const [expandedTxnStatementIds, setExpandedTxnStatementIds] = useState<Set<string>>(new Set());
  const [expandedAvgAuditIds, setExpandedAvgAuditIds] = useState<Set<string>>(new Set());
  const [editingCategoryTxnId, setEditingCategoryTxnId] = useState<string | null>(null);
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [categoryMonth, setCategoryMonth] = useState<string>("total");
  const { data: currentUser } = useQuery<{ companyName?: string | null; username?: string }>({
    queryKey: ["/api/me"],
  });

  const dismissedNoticesStorageKey = useMemo(
    () => `bankHealthDismissedNotices:${currentUser?.username ?? "anon"}`,
    [currentUser?.username],
  );
  const [dismissedNotices, setDismissedNotices] = useState<Record<string, boolean>>({});
  useEffect(() => {
    try {
      const raw = localStorage.getItem(dismissedNoticesStorageKey);
      setDismissedNotices(raw ? JSON.parse(raw) : {});
    } catch {
      setDismissedNotices({});
    }
  }, [dismissedNoticesStorageKey]);
  const dismissNotice = useCallback((key: string) => {
    setDismissedNotices((prev) => {
      const next = { ...prev, [key]: true };
      try {
        localStorage.setItem(dismissedNoticesStorageKey, JSON.stringify(next));
      } catch {
        /* ignore persistence failures */
      }
      return next;
    });
  }, [dismissedNoticesStorageKey]);

  const { data: statements = [], isLoading: statementsLoading } = useQuery<BankStatement[]>({
    queryKey: ["/api/bank-statements"],
  });

  const { data: allTransactions = [] } = useQuery<BankTransaction[]>({
    queryKey: ["/api/bank-transactions"],
  });

  const isCustomRangeInvalid = computeIsCustomRangeInvalid(dateRange, customMode, customFrom, customTo);

  // Years offered in the Custom dropdowns: every year present in the user's
  // statements, plus the current year and the years currently selected, so the
  // Year <Select> always has a matching option to display. Descending order.
  const availableYears = useMemo(
    () => computeAvailableYears(statements.map(getBankStatementMonthKey), customFrom, customTo),
    [statements, customFrom, customTo],
  );

  const cutoffKey = useMemo(
    () => (dateRange === "custom" ? customFrom : dateRange === "all" ? "0000-00" : getDateRangeCutoff(dateRange)),
    [dateRange, customFrom],
  );

  const filteredStatements = useMemo(() => {
    return statements.filter((s) => {
      const key = getBankStatementMonthKey(s);
      if (!key) return true;
      if (dateRange === "all") return true;
      if (dateRange === "custom") return key >= customFrom && key <= customTo;
      return key >= cutoffKey;
    });
  }, [statements, cutoffKey, dateRange, customFrom, customTo]);

  const filteredTransactions = useMemo(() => {
    const filteredIds = new Set(filteredStatements.map((s) => s.id));
    return allTransactions.filter((t) => t.bankStatementId && filteredIds.has(t.bankStatementId));
  }, [allTransactions, filteredStatements]);

  const sortedFilteredStatements = useMemo(() => {
    return [...filteredStatements].sort((a, b) => {
      const dateA = a.accountPeriodEnd ?? a.accountPeriodStart ?? a.createdAt ?? "";
      const dateB = b.accountPeriodEnd ?? b.accountPeriodStart ?? b.createdAt ?? "";
      return dateB < dateA ? -1 : dateB > dateA ? 1 : 0;
    });
  }, [filteredStatements]);

  // ── PHASE 14C: presentation-only readiness tiers ──────────────────────────
  // Statements fall into three display tiers derived purely on the frontend from
  // signals we already receive (needsReview / transactionExtractionFailed / the
  // printed balance equation). No backend, validation, or persistence change.
  //   Tier 1 full_verified                          → full dashboard
  //   Tier 2 summary_verified_limited_transactions  → summary dashboard, calm note
  //   Tier 3 review_required                         → review-required state
  const txnCountByStmt = useMemo(() => {
    const m: Record<string, number> = {};
    for (const t of allTransactions) {
      if (t.bankStatementId) m[t.bankStatementId] = (m[t.bankStatementId] ?? 0) + 1;
    }
    return m;
  }, [allTransactions]);

  const readinessById = useMemo(() => {
    const m: Record<string, BankStatementUiReadiness> = {};
    for (const s of statements) {
      m[s.id] = deriveBankStatementUiReadiness(
        deriveReadinessInputFromStatement(s, {
          hasTransactions: (txnCountByStmt[s.id] ?? 0) > 0,
        }),
      );
    }
    return m;
  }, [statements, txnCountByStmt]);

  const tierOf = useCallback(
    (id: string) => readinessById[id]?.tier ?? "review_required",
    [readinessById],
  );

  const reviewRequiredStatements = useMemo(
    () => filteredStatements.filter((s) => tierOf(s.id) === "review_required"),
    [filteredStatements, tierOf],
  );
  const reviewRequiredCount = reviewRequiredStatements.length;
  const allReviewRequired =
    filteredStatements.length > 0 && reviewRequiredCount === filteredStatements.length;

  const summaryLimitedStatements = useMemo(
    () => filteredStatements.filter((s) => tierOf(s.id) === "summary_verified_limited_transactions"),
    [filteredStatements, tierOf],
  );
  const summaryLimitedCount = summaryLimitedStatements.length;

  const hasFullVerified = useMemo(
    () => filteredStatements.some((s) => tierOf(s.id) === "full_verified"),
    [filteredStatements, tierOf],
  );

  // Summary-verified (Tier 1 + Tier 2) statements feed the summary charts/KPIs;
  // Tier 3 (unverified summary money) is excluded so it cannot pollute headline figures.
  const summaryVerifiedStatements = useMemo(
    () => filteredStatements.filter((s) => tierOf(s.id) !== "review_required"),
    [filteredStatements, tierOf],
  );
  const sortedSummaryVerifiedStatements = useMemo(
    () =>
      [...summaryVerifiedStatements].sort((a, b) => {
        const dateA = a.accountPeriodEnd ?? a.accountPeriodStart ?? a.createdAt ?? "";
        const dateB = b.accountPeriodEnd ?? b.accountPeriodStart ?? b.createdAt ?? "";
        return dateB < dateA ? -1 : dateB > dateA ? 1 : 0;
      }),
    [summaryVerifiedStatements],
  );
  // Only Tier 1 statements have a verified transaction ledger, so only their
  // transactions may feed transaction-derived charts (category/merchant/count).
  const fullVerifiedStatementIds = useMemo(
    () => new Set(filteredStatements.filter((s) => tierOf(s.id) === "full_verified").map((s) => s.id)),
    [filteredStatements, tierOf],
  );
  const fullVerifiedTransactions = useMemo(
    () => filteredTransactions.filter((t) =>
      t.bankStatementId &&
      fullVerifiedStatementIds.has(t.bankStatementId) &&
      isUsableBankTransaction(t),
    ),
    [filteredTransactions, fullVerifiedStatementIds],
  );

  const kpis = useMemo(() => {
    const headline = computeBankHealthHeadlineMetrics(summaryVerifiedStatements);
    if (!headline) return null;

    const debitTxns = fullVerifiedTransactions.filter((t) => t.type === "debit");
    const hasTransactionDebits = debitTxns.length > 0;

    const categoryTotals: Record<string, number> = {};
    if (hasTransactionDebits) {
      for (const t of debitTxns) {
        const cat = t.category ?? "Other";
        const amount = parseN(t.amount);
        if (amount === null || !Number.isFinite(amount)) continue;
        categoryTotals[cat] = (categoryTotals[cat] ?? 0) + amount;
      }
    } else if (headline.totalDebits > 0) {
      categoryTotals["Unitemized (statement total)"] = headline.totalDebits;
    }

    return {
      ...headline,
      categoryTotals,
      hasTransactionDebits,
    };
  }, [summaryVerifiedStatements, fullVerifiedTransactions]);

  const categoryMonthOptions = useMemo(() => {
    const months = new Set<string>();
    for (const t of fullVerifiedTransactions) {
      if (t.transactionDate) months.add(String(t.transactionDate).slice(0, 7));
    }
    return Array.from(months).sort().reverse();
  }, [fullVerifiedTransactions]);

  // Reset month selection when the date-range filter changes so stale months don't linger.
  useEffect(() => {
    setCategoryMonth("total");
  }, [dateRange, customFrom, customTo]);

  const categoryChartData = useMemo(() => {
    // Returned/rejected items (e.g. an NSF-bounced check) are excluded from the
    // bank's totals, so they must not appear in the spend-by-category breakdown.
    const debitTxns = fullVerifiedTransactions.filter(
      (t) => t.type === "debit" && t.includedInStatementTotals !== false && isUsableBankTransaction(t),
    );
    const monthFiltered = categoryMonth === "total"
      ? debitTxns
      : debitTxns.filter((t) => String(t.transactionDate ?? "").slice(0, 7) === categoryMonth);

    if (monthFiltered.length > 0) {
      const totals: Record<string, number> = {};
      for (const t of monthFiltered) {
        const cat = t.category ?? "Other";
        const amount = parseN(t.amount);
        if (amount === null || !Number.isFinite(amount)) continue;
        totals[cat] = (totals[cat] ?? 0) + amount;
      }
      return Object.entries(totals)
        .filter(([, v]) => v > 0)
        .sort((a, b) => b[1] - a[1])
        .map(([name, value]) => ({ name, value, color: CATEGORY_CHART_HEX[name] ?? "#9ca3af" }));
    }

    // Fallback: no per-transaction data — use statement-level totals for the "Total" view only.
    if (categoryMonth === "total" && kpis?.categoryTotals) {
      return Object.entries(kpis.categoryTotals)
        .filter(([, v]) => v > 0)
        .sort((a, b) => b[1] - a[1])
        .map(([name, value]) => ({ name, value, color: CATEGORY_CHART_HEX[name] ?? "#9ca3af" }));
    }

    return [];
  }, [fullVerifiedTransactions, categoryMonth, kpis]);

  const handleCategoryClick = useCallback((category: string) => {
    const next = category === selectedCategory ? null : category;
    setSelectedCategory(next);
    if (next) {
      setExpandedStatements(true);
      setExpandedTxnStatementIds((prev) => {
        const ids = new Set(prev);
        for (const stmt of filteredStatements) {
          if (tierOf(stmt.id) !== "full_verified") continue;
          const hasCat = fullVerifiedTransactions.some(
            (t) => t.bankStatementId === stmt.id && t.type === "debit" && (t.category ?? "Other") === next
          );
          if (hasCat) ids.add(stmt.id);
        }
        return ids;
      });
    }
  }, [selectedCategory, filteredStatements, fullVerifiedTransactions, tierOf]);

  const kpiCards = useMemo(() => {
    if (!kpis) return [];

    const getHealth = (key: string): KpiHealth => {
      if (key === "cashOnHand") {
        if (kpis.cashOnHand == null) return "red";
        if (kpis.monthlyBurnRate > 0) {
          const days = (kpis.cashOnHand / kpis.monthlyBurnRate) * 30;
          if (days >= 30) return "green";
          if (days >= 15) return "yellow";
          return "red";
        }
        if (kpis.cashOnHand >= 50000) return "green";
        if (kpis.cashOnHand >= 20000) return "yellow";
        return "red";
      }
      if (key === "cashFlowRatio") {
        if (kpis.cashFlowRatio == null) return "red";
        if (kpis.cashFlowRatio >= 1.2) return "green";
        if (kpis.cashFlowRatio >= 1.0) return "yellow";
        return "red";
      }
      if (key === "daysOfCash") {
        if (kpis.daysOfCash == null) return "red";
        if (kpis.daysOfCash >= 30) return "green";
        if (kpis.daysOfCash >= 15) return "yellow";
        return "red";
      }
      if (key === "burnRate") {
        if (kpis.monthlyBurnRate === 0) return "green";
        const ratio = kpis.cashOnHand != null ? kpis.cashOnHand / kpis.monthlyBurnRate : 0;
        if (ratio >= 3) return "green";
        if (ratio >= 1) return "yellow";
        return "red";
      }
      return "green";
    };

    const daysLabel = kpis.daysOfCash != null
      ? t("bank:kpi.coversDays", { days: Math.round(kpis.daysOfCash) })
      : t("bank:kpi.unableToCalculate");

    const cashBenchmark = kpis.daysOfCash != null
      ? t("bank:kpi.coversDaysAbout", { days: Math.round(kpis.daysOfCash) })
      : t("bank:kpi.uploadMoreCoverage");

    return [
      {
        key: "cashOnHand",
        title: t("bank:kpi.cashOnHand.title"),
        value: fmt$(kpis.cashOnHand),
        health: getHealth("cashOnHand"),
        benchmark: cashBenchmark,
        icon: DollarSign,
        description: t("bank:kpi.cashOnHand.description"),
      },
      {
        key: "burnRate",
        title: t("bank:kpi.burnRate.title"),
        value: fmt$(kpis.monthlyBurnRate),
        health: getHealth("burnRate"),
        benchmark: t("bank:kpi.burnRate.benchmark"),
        icon: TrendingDown,
        description: t("bank:kpi.burnRate.description"),
      },
      {
        key: "cashFlowRatio",
        title: t("bank:kpi.cashFlowRatio.title"),
        value: kpis.cashFlowRatio != null ? `${kpis.cashFlowRatio.toFixed(2)}x` : "—",
        health: getHealth("cashFlowRatio"),
        benchmark: t("bank:kpi.cashFlowRatio.benchmark"),
        icon: Activity,
        description: t("bank:kpi.cashFlowRatio.description"),
      },
      {
        key: "daysOfCash",
        title: t("bank:kpi.daysOfCash.title"),
        value: kpis.daysOfCash != null ? t("bank:kpi.daysOfCash.value", { days: Math.round(kpis.daysOfCash) }) : "—",
        health: getHealth("daysOfCash"),
        benchmark: daysLabel,
        icon: TrendingUp,
        description: t("bank:kpi.daysOfCash.description"),
      },
    ];
  }, [kpis, t]);

  function safeMonthKey(dateStr: string | null | undefined): { key: string; label: string } | null {
    if (!dateStr) return null;
    const isoMatch = dateStr.match(/^(\d{4})-(\d{2})/);
    if (isoMatch) {
      const year = isoMatch[1];
      const monthIdx = parseInt(isoMatch[2], 10) - 1;
      if (monthIdx >= 0 && monthIdx <= 11) {
        return { key: `${year}-${isoMatch[2]}`, label: fmtMonth(`${year}-${isoMatch[2]}`) };
      }
    }
    const d = new Date(dateStr);
    if (!isNaN(d.getTime())) {
      const year = d.getUTCFullYear();
      const monthIdx = d.getUTCMonth();
      const mm = String(monthIdx + 1).padStart(2, "0");
      return { key: `${year}-${mm}`, label: fmtMonth(`${year}-${mm}`) };
    }
    return null;
  }

  const monthlyTrendData = useMemo(() => {
    const txnCountByStatementId: Record<string, number> = {};
    for (const t of fullVerifiedTransactions) {
      if (!t.bankStatementId) continue;
      txnCountByStatementId[t.bankStatementId] = (txnCountByStatementId[t.bankStatementId] ?? 0) + 1;
    }

    const monthMap: Record<string, {
      label: string;
      txnCount: number;
      stmtCredits: number;
      stmtDebits: number;
      closingBalance: number;
      moneyOutAvailable: boolean;
    }> = {};

    for (const s of summaryVerifiedStatements) {
      const parsed = safeMonthKey(getBankStatementMonthKey(s));
      if (!parsed) continue;
      const { key, label } = parsed;
      if (!monthMap[key]) {
        monthMap[key] = {
          label,
          txnCount: 0,
          stmtCredits: 0,
          stmtDebits: 0,
          closingBalance: 0,
          moneyOutAvailable: true,
        };
      }
      const entry = monthMap[key];
      entry.txnCount += txnCountByStatementId[s.id ?? ""] ?? 0;
      entry.stmtCredits += effectiveCredits(s) ?? 0;
      // Stated money-out (withdrawals + checks + service fees). Mirrors the KPI logic.
      const moneyOut = effectiveMoneyOut(s);
      if (moneyOut == null) entry.moneyOutAvailable = false;
      else entry.stmtDebits += moneyOut;
      // Sum closing balances within a month so multiple accounts (e.g. different
      // banks) combine into the dealer's total ending balance for that month.
      entry.closingBalance += parseN(s.closingBalance) ?? 0;
    }

    return Object.entries(monthMap)
      .sort(([a], [b]) => a.localeCompare(b))
      .filter(([, entry]) => entry.moneyOutAvailable)
      .map(([, entry]) => {
        const revenue = entry.stmtCredits;
        const expenses = entry.stmtDebits;
        const net = revenue - expenses;
        const balance = entry.closingBalance;
        const ratio = expenses > 0 ? revenue / expenses : null;
        const usingStatementFallback = entry.txnCount === 0;
        return { month: entry.label, revenue, expenses, net, balance, ratio, usingStatementFallback };
      });
  }, [summaryVerifiedStatements, fullVerifiedTransactions]);

  const handleExportPDF = useCallback(() => {
    const dealerName = currentUser?.companyName || currentUser?.username || t("bank:pdf.dealerDefault");
    const dateStr = new Date().toLocaleDateString(intlLocale(), { year: "numeric", month: "long", day: "numeric" });
    const rangeLabel = dateRange === "custom"
      ? (customMode === "month"
          ? formatMonthYear(customFrom)
          : `${formatMonthYear(customFrom)} – ${formatMonthYear(customTo)}`)
      : t(`bank:dateRange.pdf.${dateRange}`);

    const doc = new jsPDF({ orientation: "portrait", unit: "mm", format: "letter" });
    const pageW = doc.internal.pageSize.getWidth();
    const margin = 16;
    let y = margin;

    doc.setFontSize(18);
    doc.setFont("helvetica", "bold");
    doc.text(t("bank:pdf.reportTitle", { range: rangeLabel }), margin, y);
    y += 8;

    doc.setFontSize(11);
    doc.setFont("helvetica", "normal");
    doc.setTextColor(80, 80, 80);
    doc.text(dealerName, margin, y);
    y += 6;
    doc.text(t("bank:pdf.generated", { date: dateStr }), margin, y);
    y += 10;

    doc.setDrawColor(200, 200, 200);
    doc.line(margin, y, pageW - margin, y);
    y += 8;

    doc.setFontSize(13);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(30, 30, 30);
    doc.text(t("bank:pdf.cashFlowKpis"), margin, y);
    y += 6;

    if (kpis) {
      const healthLabel = (h: KpiHealth) => h === "green" ? t("bank:pdf.healthGood") : h === "yellow" ? t("bank:pdf.healthFair") : t("bank:pdf.healthAtRisk");
      const kpiRows = kpiCards.map((k) => [k.title, k.value, healthLabel(k.health), k.benchmark]);
      autoTable(doc, {
        startY: y,
        head: [[t("bank:pdf.colMetric"), t("bank:pdf.colValue"), t("bank:pdf.colStatus"), t("bank:pdf.colBenchmark")]],
        body: kpiRows,
        margin: { left: margin, right: margin },
        styles: { fontSize: 9, cellPadding: 3 },
        headStyles: { fillColor: [41, 98, 255], textColor: 255, fontStyle: "bold" },
        columnStyles: {
          0: { fontStyle: "bold", cellWidth: 45 },
          1: { cellWidth: 30 },
          2: { cellWidth: 22 },
          3: { cellWidth: "auto" },
        },
        didParseCell: (data) => {
          if (data.section === "body" && data.column.index === 2) {
            const val = String(data.cell.raw);
            if (val === t("bank:pdf.healthGood")) data.cell.styles.textColor = [16, 185, 129];
            else if (val === t("bank:pdf.healthFair")) data.cell.styles.textColor = [202, 138, 4];
            else data.cell.styles.textColor = [220, 38, 38];
          }
        },
      });
      interface JsPDFWithAutoTable extends jsPDF { lastAutoTable: { finalY: number } }
      y = (doc as JsPDFWithAutoTable).lastAutoTable.finalY + 10;
    }

    if (monthlyTrendData.length > 0) {
      doc.setFontSize(13);
      doc.setFont("helvetica", "bold");
      doc.setTextColor(30, 30, 30);
      doc.text(t("bank:pdf.monthlyTrend"), margin, y);
      y += 4;

      const trendRows = monthlyTrendData.map((row) => [
        row.month,
        fmt$(row.balance),
        fmt$(row.revenue),
        fmt$(row.expenses),
        fmt$(row.net),
      ]);

      autoTable(doc, {
        startY: y,
        head: [[t("bank:pdf.colMonth"), t("bank:pdf.colClosingBalance"), t("bank:pdf.colTotalRevenue"), t("bank:pdf.colTotalExpenses"), t("bank:pdf.colNetCashFlow")]],
        body: trendRows,
        margin: { left: margin, right: margin },
        styles: { fontSize: 9, cellPadding: 3 },
        headStyles: { fillColor: [41, 98, 255], textColor: 255, fontStyle: "bold" },
        didParseCell: (data) => {
          if (data.section === "body" && data.column.index === 4) {
            const raw = monthlyTrendData[data.row.index]?.net ?? 0;
            data.cell.styles.textColor = raw >= 0 ? [16, 185, 129] : [220, 38, 38];
          }
        },
      });

      interface JsPDFWithAutoTable2 extends jsPDF { lastAutoTable: { finalY: number } }
      y = (doc as JsPDFWithAutoTable2).lastAutoTable.finalY + 8;

      // Build a line chart (closing balance + net cash flow) on an off-screen canvas
      if (monthlyTrendData.length > 1) {
        const LINE_CANVAS_W = 900;
        const LINE_CANVAS_H = 340;

        const lineCanvas = document.createElement("canvas");
        lineCanvas.width = LINE_CANVAS_W;
        lineCanvas.height = LINE_CANVAS_H;

        const lineChart = new Chart(lineCanvas, {
          type: "line",
          data: {
            labels: monthlyTrendData.map((r) => r.month),
            datasets: [
              {
                label: t("bank:pdf.legendClosingBalance"),
                data: monthlyTrendData.map((r) => r.balance),
                borderColor: "#2962ff",
                backgroundColor: "rgba(41,98,255,0.10)",
                pointBackgroundColor: "#2962ff",
                borderWidth: 2,
                pointRadius: 4,
                fill: false,
                tension: 0.3,
              },
              {
                label: t("bank:pdf.legendNetCashFlow"),
                data: monthlyTrendData.map((r) => r.net),
                borderColor: "#00b0ff",
                backgroundColor: "rgba(0,176,255,0.08)",
                pointBackgroundColor: "#00b0ff",
                borderWidth: 2,
                pointRadius: 4,
                fill: false,
                tension: 0.3,
                borderDash: [5, 3],
              },
            ],
          },
          options: {
            animation: false,
            responsive: false,
            plugins: {
              legend: {
                display: true,
                position: "top",
                labels: { font: { size: 12 }, boxWidth: 18 },
              },
              tooltip: { enabled: false },
            },
            scales: {
              x: {
                ticks: { font: { size: 11 } },
                grid: { color: "#e5e7eb" },
              },
              y: {
                ticks: {
                  callback: (v) => `$${Number(v).toLocaleString()}`,
                  font: { size: 11 },
                },
                grid: { color: "#e5e7eb" },
              },
            },
            layout: { padding: { top: 8, right: 16, bottom: 8, left: 8 } },
          },
        });

        const lineImgData = lineChart.toBase64Image("image/png", 1);
        lineChart.destroy();

        const pdfLineW = pageW - margin * 2;
        const pdfLineH = (LINE_CANVAS_H / LINE_CANVAS_W) * pdfLineW;
        const pageH2 = doc.internal.pageSize.getHeight();

        if (y + pdfLineH > pageH2 - margin) {
          doc.addPage();
          y = margin;
        }
        const safeLineH = Math.min(pdfLineH, pageH2 - margin * 2);
        const safeLineW = safeLineH === pdfLineH ? pdfLineW : (LINE_CANVAS_W / LINE_CANVAS_H) * safeLineH;

        doc.addImage(lineImgData, "PNG", margin, y, safeLineW, safeLineH);
        y += safeLineH + 10;
      }
    }

    if (kpis?.categoryTotals) {
      const catEntries = Object.entries(kpis.categoryTotals)
        .filter(([, amount]) => amount > 0)
        .sort(([, a], [, b]) => b - a);

      if (catEntries.length > 0) {
        const totalDebits = catEntries.reduce((sum, [, amount]) => sum + amount, 0);

        doc.setFontSize(13);
        doc.setFont("helvetica", "bold");
        doc.setTextColor(30, 30, 30);
        doc.text(t("bank:pdf.spendingByCategory"), margin, y);
        y += 4;

        const catRows = catEntries.map(([category, amount]) => [
          categoryLabel(category, t),
          fmt$(amount),
          totalDebits > 0 ? `${((amount / totalDebits) * 100).toFixed(1)}%` : "—",
        ]);

        autoTable(doc, {
          startY: y,
          head: [[t("bank:pdf.colCategory"), t("bank:pdf.colTotalSpend"), t("bank:pdf.colPctOfDebits")]],
          body: catRows,
          margin: { left: margin, right: margin },
          styles: { fontSize: 9, cellPadding: 3 },
          headStyles: { fillColor: [41, 98, 255], textColor: 255, fontStyle: "bold" },
          columnStyles: {
            0: { fontStyle: "bold", cellWidth: "auto" },
            1: { cellWidth: 35 },
            2: { cellWidth: 35 },
          },
        });

        interface JsPDFWithAutoTable3 extends jsPDF { lastAutoTable: { finalY: number } }
        y = (doc as JsPDFWithAutoTable3).lastAutoTable.finalY + 8;

        // Build a horizontal bar chart using Chart.js on an off-screen canvas
        const BAR_COLORS = [
          "#2962ff", "#00b0ff", "#00bfa5", "#69f0ae", "#ffab40",
          "#ff6d00", "#ea80fc", "#b388ff", "#80d8ff", "#ccff90",
        ];

        const CANVAS_W = 900;
        const ROW_H = 36;
        const CANVAS_H = Math.max(200, catEntries.length * ROW_H + 80);

        const canvas = document.createElement("canvas");
        canvas.width = CANVAS_W;
        canvas.height = CANVAS_H;

        const chartColors = catEntries.map((_, i) => BAR_COLORS[i % BAR_COLORS.length]);

        const chart = new Chart(canvas, {
          type: "bar",
          data: {
            labels: catEntries.map(([cat]) => categoryLabel(cat, t)),
            datasets: [{
              label: t("bank:pdf.spendAxis"),
              data: catEntries.map(([, amt]) => amt),
              backgroundColor: chartColors,
              borderColor: chartColors,
              borderWidth: 1,
              borderRadius: 4,
            }],
          },
          options: {
            indexAxis: "y",
            animation: false,
            responsive: false,
            plugins: {
              legend: { display: false },
              tooltip: { enabled: false },
            },
            scales: {
              x: {
                ticks: {
                  callback: (v) => `$${Number(v).toLocaleString()}`,
                  font: { size: 11 },
                },
                grid: { color: "#e5e7eb" },
              },
              y: {
                ticks: { font: { size: 12 } },
                grid: { display: false },
              },
            },
            layout: { padding: { right: 16 } },
          },
        });

        const imgData = chart.toBase64Image("image/png", 1);
        chart.destroy();

        // Scale chart to full page width in the PDF
        const pdfChartW = pageW - margin * 2;
        const pdfChartH = (CANVAS_H / CANVAS_W) * pdfChartW;
        const pageH = doc.internal.pageSize.getHeight();

        // Start a new page if chart won't fit; scale down if taller than printable area
        if (y + pdfChartH > pageH - margin) {
          doc.addPage();
          y = margin;
        }
        const safeChartH = Math.min(pdfChartH, pageH - margin * 2);
        const safeChartW = safeChartH === pdfChartH ? pdfChartW : (CANVAS_W / CANVAS_H) * safeChartH;

        doc.addImage(imgData, "PNG", margin, y, safeChartW, safeChartH);
        y += safeChartH + 6;
      }
    }

    const safeName = dealerName.replace(/[^a-z0-9]/gi, "-").toLowerCase();
    doc.save(`bank-health-${safeName}-${new Date().toISOString().slice(0, 10)}.pdf`);
  }, [currentUser, kpis, kpiCards, monthlyTrendData, dateRange, customFrom, customTo, customMode, t]);

  const avgDailyBalanceData = useMemo(() => {
    const txnByStatementId: Record<string, BankTransaction[]> = {};
    for (const t of fullVerifiedTransactions) {
      if (!t.bankStatementId) continue;
      if (!txnByStatementId[t.bankStatementId]) txnByStatementId[t.bankStatementId] = [];
      txnByStatementId[t.bankStatementId].push(t);
    }

    // PHASE 14C average-balance hard rule: only show a PROVEN average — a printed
    // statement average ("From Statement"), or a real day-by-day average computed
    // from a verified (Tier 1) transaction ledger ("Calculated"). Statement-provided
    // averages are labeled by provenance: verified sources (Daily Balance Summary /
    // running balances / dealer-confirmed) read as such, while a model_estimate or
    // unknown-provenance (legacy null source) average is labeled "Estimated — not
    // verified" rather than presented as trustworthy.
    const results: Array<{
      key: string;
      month: string;
      avgDailyBalance: number;
      daysInPeriod: number | null;
      source: string;
    }> = [];

    for (const s of summaryVerifiedStatements) {
      const parsed = safeMonthKey(getBankStatementMonthKey(s));
      if (!parsed) continue;

      const opening = parseN(s.openingBalance);

      const stmtAvg = effectiveAvgBalance(s);
      if (stmtAvg != null) {
        const src = s.dealerVerifiedAt
          ? t("bank:avgSource.dealerVerified")
          : !s.avgDailyBalanceVerified
            ? t("bank:avgSource.estimatedNotVerified")
            : s.avgDailyBalanceSource === "daily_balance_summary_table"
              ? t("bank:avgSource.dailyBalanceSummary")
              : s.avgDailyBalanceSource === "running_balances"
                ? t("bank:avgSource.runningBalances")
                : s.avgDailyBalanceSource === "stated_on_statement"
                  ? t("bank:avgSource.fromStatement")
                  : t("bank:avgSource.verified");
        results.push({
          key: parsed.key,
          month: parsed.label,
          avgDailyBalance: stmtAvg,
          daysInPeriod: null,
          source: src,
        });
        continue;
      }

      // A computed average is only trustworthy when the transaction ledger is
      // verified (Tier 1). Tier 2 statements fall through with no average rather
      // than presenting a number derived from incomplete transactions.
      if (!fullVerifiedStatementIds.has(s.id)) continue;

      const periodStart = s.accountPeriodStart;
      const periodEnd = s.accountPeriodEnd;
      const txns = txnByStatementId[s.id] ?? [];

      if (opening != null && periodStart && periodEnd && txns.length > 0) {
        const startDate = new Date(periodStart + "T00:00:00Z");
        const endDate = new Date(periodEnd + "T00:00:00Z");
        const daysInPeriod = Math.round((endDate.getTime() - startDate.getTime()) / 86400000) + 1;

        if (daysInPeriod > 0) {
          const txnsByDay: Record<string, number> = {};
          for (const t of txns) {
            if (!t.transactionDate) continue;
            const dayKey = t.transactionDate.slice(0, 10);
            const amt = parseN(t.amount);
            if (amt === null || !Number.isFinite(amt)) continue;
            const signed = t.type === "credit" ? amt : -amt;
            txnsByDay[dayKey] = (txnsByDay[dayKey] ?? 0) + signed;
          }

          let runningBalance = opening;
          let balanceSum = 0;
          for (let i = 0; i < daysInPeriod; i++) {
            const d = new Date(startDate.getTime() + i * 86400000);
            const dayKey = d.toISOString().slice(0, 10);
            runningBalance += txnsByDay[dayKey] ?? 0;
            balanceSum += runningBalance;
          }

          results.push({
            key: parsed.key,
            month: parsed.label,
            avgDailyBalance: balanceSum / daysInPeriod,
            daysInPeriod,
            source: t("bank:avgSource.calculated"),
          });
          continue;
        }
      }
    }

    // Group per-statement averages by month and SUM within a month, so multiple
    // accounts (e.g. different banks) combine into the dealer's total average daily
    // balance for that month. Mixed provenance across accounts reads as "Combined".
    const byMonth: Record<string, {
      key: string;
      month: string;
      avgDailyBalance: number;
      count: number;
      daysInPeriod: number | null;
      sources: Set<string>;
    }> = {};
    for (const r of results) {
      if (!byMonth[r.key]) {
        byMonth[r.key] = { key: r.key, month: r.month, avgDailyBalance: 0, count: 0, daysInPeriod: r.daysInPeriod, sources: new Set() };
      }
      const m = byMonth[r.key];
      m.avgDailyBalance += r.avgDailyBalance;
      m.count += 1;
      m.sources.add(r.source);
    }
    return Object.values(byMonth)
      .map((m) => ({
        key: m.key,
        month: m.month,
        avgDailyBalance: m.avgDailyBalance,
        // Days-in-period only applies to a single account's statement; once accounts
        // are summed for a month it's no longer a single period, so hide it.
        daysInPeriod: m.count === 1 ? m.daysInPeriod : null,
        source: m.sources.size === 1 ? Array.from(m.sources)[0] : t("bank:avgSource.combined"),
      }))
      .sort((a, b) => a.key.localeCompare(b.key));
  }, [summaryVerifiedStatements, fullVerifiedTransactions, fullVerifiedStatementIds]);

  const deleteStatementMutation = useMutation({
    mutationFn: async (id: string) => {
      await apiRequest("DELETE", `/api/bank-statements/${id}`);
      return id;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["/api/bank-statements"] });
      qc.invalidateQueries({ queryKey: ["/api/bank-transactions"] });
      toast({ title: t("bank:toast.statementDeleted") });
    },
    onError: () => toast({ title: t("bank:toast.failedToDelete"), variant: "destructive" }),
  });

  const reExtractMutation = useMutation({
    mutationFn: async (id: string) => {
      const res = await apiRequest("POST", `/api/bank-statements/${id}/re-extract`);
      if (!res.ok) {
        const errJson = await res.json().catch(() => ({}));
        throw new Error((errJson as any).message || t("bank:error.reExtractionFailed"));
      }
      const { jobId } = await res.json() as { jobId: string };

      for (let attempt = 0; attempt < 120; attempt++) {
        await new Promise(r => setTimeout(r, 5000));
        const statusRes = await fetch(`/api/bank-statements/upload/status/${jobId}`, { credentials: "include" });
        const statusJson = await statusRes.json() as any;
        if (statusJson.status === "done") {
          const count = statusJson.result?.transactionCount ?? 0;
          return { success: count > 0, transactionCount: count };
        }
        if (statusJson.status === "error") {
          throw new Error(statusJson.error || statusJson.result?.error || t("bank:error.reExtractionFailed"));
        }
      }
      throw new Error(t("bank:error.reExtractionTimedOut"));
    },
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: ["/api/bank-statements"] });
      qc.invalidateQueries({ queryKey: ["/api/bank-transactions"] });
      if (data.success && data.transactionCount > 0) {
        toast({ title: t("bank:toast.transactionsExtracted"), description: t("bank:toast.transactionsExtractedDesc", { count: data.transactionCount }) });
      } else {
        toast({ title: t("bank:toast.extractionCompleted"), description: t("bank:toast.extractionCompletedDesc"), variant: "destructive" });
      }
    },
    onError: (err: any) => toast({ title: t("bank:toast.reExtractionFailed"), description: err.message ?? t("bank:toast.reExtractionFailedDesc"), variant: "destructive" }),
  });

  const verifySummaryMutation = useMutation({
    mutationFn: async ({ id, values }: { id: string; values: { credits: string; debits: string; avgBalance: string } }) => {
      const res = await apiRequest("PATCH", `/api/bank-statements/${id}/summary`, {
        credits: values.credits,
        debits: values.debits,
        avgBalance: values.avgBalance.trim() === "" ? null : values.avgBalance,
      });
      return res.json();
    },
    onSuccess: () => {
      toast({ title: t("bank:toast.summarySaved"), description: t("bank:toast.summarySavedDesc") });
      qc.invalidateQueries({ queryKey: ["/api/bank-statements"] });
    },
    onError: (e: any) => {
      toast({ title: t("bank:toast.couldNotSaveSummary"), description: e?.message ?? t("bank:toast.pleaseTryAgain"), variant: "destructive" });
    },
  });

  const updateCategoryMutation = useMutation({
    mutationFn: async ({ txnId, category, payee }: { txnId: string; category: string; payee?: string | null }) => {
      const res = await apiRequest("PATCH", `/api/bank-transactions/${txnId}/category`, {
        category,
        ...(payee ? { payee } : {}),
      });
      return res.json();
    },
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: ["/api/bank-transactions"] });
      toast({
        title: t("bank:toast.categoryUpdated"),
        description: vars.payee
          ? t("bank:toast.categoryRuleSaved", { payee: vars.payee })
          : undefined,
      });
      setEditingCategoryTxnId(null);
    },
    onError: () => {
      toast({ title: t("bank:toast.failedToUpdateCategory"), variant: "destructive" });
      setEditingCategoryTxnId(null);
    },
  });

  // Core polling loop — survives navigation by storing jobId in localStorage.
  // Manages uploadingStatement itself; never throws (errors are caught internally).
  const pollUploadJob = useCallback(async (jobId: string) => {
    const POLL_INTERVAL = 2000;
    const IDLE_TIMEOUT_MS = 22 * 60 * 1000;
    const MAX_POLL_NETWORK_RETRIES = 5;
    let networkRetryCount = 0;
    let lastProgressPct: number | undefined = undefined;
    let lastProgressTime = Date.now();
    try {
      while (true) {
        if (uploadCancelledRef.current) break;
        await new Promise((r) => setTimeout(r, networkRetryCount > 0 ? 6000 : POLL_INTERVAL));
        if (uploadCancelledRef.current) break;
        let statusRes: Response;
        try {
          statusRes = await fetch(`/api/bank-statements/upload/status/${jobId}`, { credentials: "include" });
        } catch {
          if (networkRetryCount < MAX_POLL_NETWORK_RETRIES) { networkRetryCount++; continue; }
          throw new Error(t("bank:error.couldNotReachServer"));
        }
        if (!statusRes.ok) {
          if (statusRes.status >= 500 && networkRetryCount < MAX_POLL_NETWORK_RETRIES) { networkRetryCount++; continue; }
          networkRetryCount = 0;
          let message = t("bank:error.serverError", { status: statusRes.status });
          try { const errJson = await statusRes.json(); message = errJson.message || errJson.error || message; } catch {}
          throw new Error(message);
        }
        networkRetryCount = 0;
        const statusJson = await statusRes.json();

        const currentPct: number | undefined = statusJson.pct;
        if (statusJson.step) setUploadStepLabel(statusJson.step);
        if (currentPct !== lastProgressPct) { lastProgressPct = currentPct; lastProgressTime = Date.now(); }

        if (statusJson.status === "done") {
          qc.invalidateQueries({ queryKey: ["/api/bank-statements"] });
          qc.invalidateQueries({ queryKey: ["/api/bank-transactions"] });
          setDateRange("all");
          const txnCount: number = statusJson.result?.transactionCount ?? 0;
          const txnFailed: boolean = statusJson.result?.transactionExtractionFailed ?? false;
          const txnError: string | undefined = statusJson.result?.transactionExtractionError;
          const description = txnCount > 0
            ? t("bank:toast.statementUploadedTxns", { count: txnCount })
            : t("bank:toast.statementProcessed");
          toast({ title: t("bank:toast.statementUploaded"), description });
          if (txnFailed) {
            toast({
              title: t("bank:toast.transactionExtractionFailed"),
              description: txnError ?? t("bank:toast.transactionExtractionFailedDesc"),
              variant: "destructive",
            });
          }
          break;
        }
        // "Cancelled by user." is a clean exit — don't surface as an error.
        if (statusJson.status === "error") {
          if (statusJson.error === "Cancelled by user.") break;
          throw new Error(statusJson.error || t("bank:error.extractionFailed"));
        }
        if (Date.now() - lastProgressTime > IDLE_TIMEOUT_MS) {
          throw new Error(t("bank:error.extractionTakingLonger"));
        }
      }
    } catch (err: any) {
      if (!uploadCancelledRef.current) {
        const msg = err.message ?? t("bank:error.uploadFailed");
        setUploadBankError(msg);
        toast({ title: t("bank:toast.uploadFailed"), description: msg, variant: "destructive" });
      }
    } finally {
      localStorage.removeItem("dm_bank_upload_job");
      activeUploadJobIdRef.current = null;
      setUploadStepLabel(null);
      setUploadingStatement(false);
    }
  }, [qc, toast]);

  // On mount: if a job was in-flight when the user navigated away, resume polling it.
  useEffect(() => {
    const storedJobId = localStorage.getItem("dm_bank_upload_job");
    if (!storedJobId) return;
    fetch(`/api/bank-statements/upload/status/${storedJobId}`, { credentials: "include" })
      .then(r => r.ok ? r.json() : null)
      .then(data => {
        if (data?.status === "processing") {
          uploadCancelledRef.current = false;
          activeUploadJobIdRef.current = storedJobId;
          if (data.step) setUploadStepLabel(data.step);
          setUploadingStatement(true);
          setUploadBankError(null);
          pollUploadJob(storedJobId);
        } else {
          localStorage.removeItem("dm_bank_upload_job");
          if (data?.status === "done") {
            qc.invalidateQueries({ queryKey: ["/api/bank-statements"] });
            qc.invalidateQueries({ queryKey: ["/api/bank-transactions"] });
            setDateRange("all");
          }
        }
      })
      .catch(() => localStorage.removeItem("dm_bank_upload_job"));
  }, []); // intentionally runs once on mount

  const handleCancelUpload = useCallback(async () => {
    uploadCancelledRef.current = true;
    const jobId = activeUploadJobIdRef.current;
    if (jobId) {
      try {
        await fetch(`/api/bank-statements/upload/${jobId}/cancel`, { method: "POST", credentials: "include" });
      } catch {}
    }
    // pollUploadJob's finally block cleans up state; we just need to ensure it exits
  }, []);

  const handleStatementUpload = useCallback(async (file: File) => {
    uploadCancelledRef.current = false;
    setUploadingStatement(true);
    setUploadBankError(null);
    let gotJobId = false;
    try {
      // Direct-to-storage: presign → PUT the bytes to object storage → finalize.
      const { uploadId } = await uploadFileDirect(file, "bank_statement");
      const res = await fetch("/api/bank-statements/upload/finalize", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ uploadId }),
        credentials: "include",
      });
      if (!res.ok) {
        let msg = t("bank:error.uploadFailed");
        try {
          const errJson = await res.json();
          msg = errJson.message || errJson.error || msg;
        } catch {}
        throw new Error(msg);
      }
      const json = await res.json();

      if (json.statement || json.extracted) {
        qc.invalidateQueries({ queryKey: ["/api/bank-statements"] });
        qc.invalidateQueries({ queryKey: ["/api/bank-transactions"] });
        setDateRange("all");
        toast({ title: t("bank:toast.statementUploaded"), description: t("bank:toast.extractionAccuracy", { score: json.statement?.accuracyScore ?? "?" }) });
        return;
      }

      const { jobId } = json;
      if (!jobId) {
        qc.invalidateQueries({ queryKey: ["/api/bank-statements"] });
        qc.invalidateQueries({ queryKey: ["/api/bank-transactions"] });
        setDateRange("all");
        toast({ title: t("bank:toast.statementUploaded") });
        return;
      }

      gotJobId = true;
      activeUploadJobIdRef.current = jobId;
      localStorage.setItem("dm_bank_upload_job", jobId);
      // Hand off to pollUploadJob — it owns setUploadingStatement(false) from here.
      await pollUploadJob(jobId);
    } catch (err: any) {
      const msg = err.message ?? t("bank:error.uploadFailed");
      setUploadBankError(msg);
      toast({ title: t("bank:toast.uploadFailed"), description: msg, variant: "destructive" });
    } finally {
      // Only reset loading state here if we never handed off to pollUploadJob
      // (which manages it in its own finally block).
      if (!gotJobId) setUploadingStatement(false);
    }
  }, [qc, toast, pollUploadJob]);

  const hasData = statements.length > 0;

  return (
    <div className="space-y-4">
      <Card
        onDragOver={(e) => { if (!uploadingStatement) e.preventDefault(); }}
        onDrop={(e) => {
          e.preventDefault();
          if (uploadingStatement) return;
          const f = e.dataTransfer.files[0];
          if (f) {
            const ext = "." + f.name.split(".").pop()!.toLowerCase();
            if ([".pdf", ".jpg", ".jpeg", ".png"].includes(ext)) handleStatementUpload(f);
          }
        }}
      >
        <CardHeader className="pb-2 pt-4 px-4">
          <CardTitle className="text-sm font-semibold flex items-center gap-2">
            <FileText className="w-4 h-4 text-primary" />
            {t("bank:upload.cardTitle")}
            {!uploadingStatement && (
              <>
                <input
                  ref={statementFileRef}
                  type="file"
                  className="hidden"
                  accept=".pdf,.jpg,.jpeg,.png"
                  data-testid="input-bank-statement-file"
                  onChange={(e) => {
                    const f = e.target.files?.[0];
                    if (f) handleStatementUpload(f);
                    e.target.value = "";
                  }}
                />
                <Button
                  variant="outline"
                  size="sm"
                  className="h-7 px-2 text-xs gap-1"
                  onClick={() => statementFileRef.current?.click()}
                  data-testid="button-add-statement"
                >
                  <Plus className="w-3 h-3" />
                  {t("bank:upload.addStatement")}
                </Button>
              </>
            )}
          </CardTitle>
          <CardDescription className="text-xs">
            {t("bank:upload.cardDescription")}
          </CardDescription>
        </CardHeader>
        {(uploadingStatement || uploadBankError) && (
          <CardContent className="px-4 pb-4 pt-0">
            <StatementUploadProgress steps={analysisStepsBank(t)} error={uploadBankError} currentStep={uploadStepLabel} />
            <div className="flex items-center gap-2 mt-2">
              {uploadBankError && (
                <Button
                  variant="outline"
                  size="sm"
                  className="h-6 text-xs px-2"
                  onClick={() => setUploadBankError(null)}
                  data-testid="button-dismiss-upload-error"
                >
                  {t("bank:upload.dismiss")}
                </Button>
              )}
              {uploadingStatement && !uploadBankError && (
                <Button
                  variant="outline"
                  size="sm"
                  className="h-6 text-xs px-2 text-destructive border-destructive/30 hover:bg-destructive/10"
                  onClick={handleCancelUpload}
                  data-testid="button-cancel-upload"
                >
                  {t("bank:upload.cancelUpload")}
                </Button>
              )}
            </div>
          </CardContent>
        )}
      </Card>

      {hasData && (
        <div className="space-y-2">
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-1 rounded-lg border bg-muted/40 p-1" data-testid="date-range-toggle">
              {dateRangeOptions(t).map((opt) => (
                <button
                  key={opt.value}
                  onClick={() => setDateRange(opt.value)}
                  className={`px-3 py-1 text-xs font-medium rounded-md transition-colors ${
                    dateRange === opt.value
                      ? "bg-background text-foreground shadow-sm border border-border"
                      : "text-muted-foreground hover:text-foreground"
                  }`}
                  data-testid={`date-range-${opt.value}`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
            <Button
              variant="outline"
              size="sm"
              className="h-8 px-3 text-xs gap-1.5 shrink-0"
              onClick={() => setShowExportConfirm(true)}
              disabled={isCustomRangeInvalid}
              title={isCustomRangeInvalid ? t("bank:filter.fixRangeBeforeExport") : undefined}
              data-testid="button-export-pdf"
            >
              <Download className="w-3.5 h-3.5" />
              {t("bank:filter.exportPdf")}
            </Button>
          </div>
          {dateRange === "custom" && (
            <div className="flex flex-col gap-2 px-1" data-testid="custom-date-range-picker">
              <div className="flex items-center gap-2 flex-wrap">
              <div className="flex items-center gap-1 rounded-lg border bg-muted/40 p-1 w-fit" data-testid="custom-mode-toggle">
                {([
                  { value: "month", label: t("bank:filter.specificMonth") },
                  { value: "range", label: t("bank:filter.dateRangeMode") },
                ] as const).map((m) => (
                  <button
                    key={m.value}
                    onClick={() => selectCustomMode(m.value)}
                    className={`px-3 py-1 text-xs font-medium rounded-md transition-colors ${
                      customMode === m.value
                        ? "bg-background text-foreground shadow-sm border border-border"
                        : "text-muted-foreground hover:text-foreground"
                    }`}
                    data-testid={`custom-mode-${m.value}`}
                  >
                    {m.label}
                  </button>
                ))}
              </div>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-7 px-2 text-xs gap-1 text-muted-foreground hover:text-foreground"
                  onClick={() => setDateRange("6mo")}
                  data-testid="button-clear-custom-filter"
                >
                  <X className="w-3.5 h-3.5" />
                  {t("bank:filter.clearFilter")}
                </Button>
              </div>

              {customMode === "month" ? (
                <div className="flex items-center gap-3 flex-wrap" data-testid="custom-month-picker">
                  <span className="text-xs text-muted-foreground shrink-0">{t("bank:filter.month")}</span>
                  <MonthYearSelect
                    value={customFrom}
                    onChange={setSpecificMonth}
                    years={availableYears}
                    testid="custom-month"
                  />
                  <span className="text-xs text-muted-foreground">{formatMonthYear(customFrom)}</span>
                </div>
              ) : (
                <div className="flex items-center gap-3 flex-wrap">
                  <span className="text-xs text-muted-foreground shrink-0">{t("bank:filter.from")}</span>
                  <MonthYearSelect
                    value={customFrom}
                    onChange={setCustomFrom}
                    years={availableYears}
                    invalid={isCustomRangeInvalid}
                    testid="custom-from"
                  />
                  <span className="text-xs text-muted-foreground shrink-0">{t("bank:filter.to")}</span>
                  <MonthYearSelect
                    value={customTo}
                    onChange={setCustomTo}
                    years={availableYears}
                    invalid={isCustomRangeInvalid}
                    testid="custom-to"
                  />
                  {!isCustomRangeInvalid && (
                    <span className="text-xs text-muted-foreground">
                      {formatMonthYear(customFrom)} – {formatMonthYear(customTo)}
                    </span>
                  )}
                </div>
              )}

              {isCustomRangeInvalid && (
                <div className="flex items-center gap-1.5 text-destructive" data-testid="custom-range-error">
                  <AlertTriangle className="w-3.5 h-3.5 shrink-0" />
                  <span className="text-xs font-medium">{t("bank:filter.fromBeforeTo")}</span>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {isCustomRangeInvalid && (
        <div
          className="flex flex-col items-center justify-center gap-2 rounded-lg border border-destructive/30 bg-destructive/5 py-10 text-center"
          data-testid="invalid-range-empty-state"
        >
          <AlertTriangle className="w-8 h-8 text-destructive/60" />
          <p className="text-sm font-semibold text-destructive">{t("bank:invalidRange.title")}</p>
          <p className="text-xs text-muted-foreground max-w-xs">
            {t("bank:invalidRange.body")}
          </p>
        </div>
      )}

      {!isCustomRangeInvalid && filteredStatements.length === 0 && statements.length > 0 && (
        <div
          className="flex flex-col items-center justify-center gap-2 rounded-lg border border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-950/30 py-8 text-center"
          data-testid="out-of-range-notice"
        >
          <AlertTriangle className="w-7 h-7 text-amber-500" />
          <p className="text-sm font-semibold text-amber-700 dark:text-amber-400">
            {t("bank:outOfRange.title")}
          </p>
          <p className="text-xs text-muted-foreground max-w-xs">
            {t("bank:outOfRange.body", { count: statements.length })}
          </p>
          <button
            className="mt-1 text-xs font-medium text-amber-700 dark:text-amber-400 underline underline-offset-2 hover:text-amber-900 dark:hover:text-amber-200"
            onClick={() => setDateRange("all")}
            data-testid="button-show-all-time"
          >
            {t("bank:outOfRange.showAll")}
          </button>
        </div>
      )}

      {!isCustomRangeInvalid && allReviewRequired && (
        <div
          className="rounded-lg border bg-muted/40 p-4 space-y-3"
          data-testid="needs-review-panel"
        >
          <div className="flex items-start gap-3">
            <Info className="w-5 h-5 text-muted-foreground shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-semibold text-foreground">
                {t("bank:needsReview.title")}
              </p>
              <p className="text-xs text-muted-foreground mt-1">
                {t("bank:needsReview.body", { count: reviewRequiredCount })}
              </p>
            </div>
          </div>
          <div className="space-y-2">
            {reviewRequiredStatements.map((s) => (
              <ReviewSummaryEditor
                key={s.id}
                statement={s}
                isSaving={verifySummaryMutation.isPending && verifySummaryMutation.variables?.id === s.id}
                onSave={(values) => verifySummaryMutation.mutate({ id: s.id, values })}
              />
            ))}
          </div>
        </div>
      )}

      {!isCustomRangeInvalid && !allReviewRequired && reviewRequiredCount > 0 && (
        <div
          className="flex items-start gap-2 rounded-lg border border-amber-300 dark:border-amber-700 bg-amber-50 dark:bg-amber-950/30 px-3 py-2.5 text-xs text-amber-700 dark:text-amber-400"
          data-testid="partial-review-warning"
        >
          <AlertTriangle className="w-3.5 h-3.5 shrink-0 mt-0.5" />
          <span>
            <span className="font-semibold">{t("bank:partialReview.heading", { reviewCount: reviewRequiredCount, count: filteredStatements.length })}</span>
            {t("bank:partialReview.suffix")}
          </span>
        </div>
      )}

      {!isCustomRangeInvalid && kpis && !allReviewRequired && (
        <div className="space-y-2">
          <div>
            <h2 className="text-sm font-semibold">{t("bank:kpis.title")}</h2>
            <p className="text-xs text-muted-foreground mt-0.5">
              {t("bank:kpis.subtitle")}
            </p>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
            {kpiCards.map(({ key: cardKey, ...kpi }) => (
              <KpiCard key={cardKey} {...kpi} />
            ))}
          </div>
        </div>
      )}

      {kpis && !allReviewRequired && monthlyTrendData.length > 0 && (
        <div className="space-y-3">
          <Card data-testid="chart-seasonality">
            <CardHeader className="pb-1 pt-3 px-4">
              <CardTitle className="text-sm font-semibold flex items-center gap-2">
                <TrendingUp className="w-4 h-4 text-primary" />
                {t("bank:trend.title")}
              </CardTitle>
              <CardDescription className="text-xs">
                {t("bank:trend.description")}
              </CardDescription>
            </CardHeader>
            <CardContent className="px-4 pb-3 space-y-3">
              <div className="grid grid-cols-3 gap-2" data-testid="seasonality-summary">
                {(() => {
                  const totalRev = monthlyTrendData.reduce((s, d) => s + d.revenue, 0);
                  const totalExp = monthlyTrendData.reduce((s, d) => s + d.expenses, 0);
                  const totalNet = totalRev - totalExp;
                  const n = monthlyTrendData.length;
                  const label = t("bank:trend.acrossMonths", { count: n });
                  const anyFallback = monthlyTrendData.some((d) => d.usingStatementFallback);
                  return (
                    <TooltipProvider>
                      <div className="rounded-lg border bg-muted/30 px-3 py-2 text-center" data-testid="seasonality-gross-revenue">
                        <div className="flex items-center justify-center gap-1 mb-1">
                          <p className="text-xs text-muted-foreground">{t("bank:trend.grossRevenue")}</p>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Info className="w-3 h-3 text-muted-foreground cursor-help" />
                            </TooltipTrigger>
                            <TooltipContent className="max-w-xs text-xs">
                              {t("bank:trend.grossRevenueTip")}
                            </TooltipContent>
                          </Tooltip>
                        </div>
                        <p className="text-base font-bold text-emerald-600">{fmtCompact(totalRev)}</p>
                        <p className="text-xs text-muted-foreground">{label}</p>
                      </div>
                      <div className="rounded-lg border bg-muted/30 px-3 py-2 text-center" data-testid="seasonality-total-expenses">
                        <div className="flex items-center justify-center gap-1 mb-1">
                          <p className="text-xs text-muted-foreground">{t("bank:trend.totalExpenses")}</p>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Info className="w-3 h-3 text-muted-foreground cursor-help" />
                            </TooltipTrigger>
                            <TooltipContent className="max-w-xs text-xs">
                              {t("bank:trend.totalExpensesTip")}
                            </TooltipContent>
                          </Tooltip>
                        </div>
                        <p className="text-base font-bold text-destructive">{fmtCompact(totalExp)}</p>
                        <p className="text-xs text-muted-foreground">{label}</p>
                      </div>
                      <div className={`rounded-lg border px-3 py-2 text-center ${totalNet >= 0 ? "bg-emerald-500/10 border-emerald-500/30" : "bg-destructive/10 border-destructive/30"}`} data-testid="seasonality-net-remaining">
                        <div className="flex items-center justify-center gap-1 mb-1">
                          <p className="text-xs text-muted-foreground">{t("bank:trend.netRemaining")}</p>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Info className="w-3 h-3 text-muted-foreground cursor-help" />
                            </TooltipTrigger>
                            <TooltipContent className="max-w-xs text-xs">
                              {t("bank:trend.netRemainingTip")}
                            </TooltipContent>
                          </Tooltip>
                        </div>
                        <p className={`text-base font-bold ${totalNet >= 0 ? "text-emerald-600" : "text-destructive"}`}>{fmtCompact(totalNet)}</p>
                        <p className="text-xs text-muted-foreground">{label}</p>
                      </div>
                      {anyFallback && !dismissedNotices.seasonalityFallback && (
                        <div className="col-span-3 flex items-start gap-2 rounded-md border border-yellow-500/30 bg-yellow-500/5 px-3 py-2 text-xs text-muted-foreground" data-testid="seasonality-fallback-notice">
                          <AlertTriangle className="w-3.5 h-3.5 text-yellow-500 shrink-0 mt-0.5" />
                          <span>{t("bank:trend.fallbackNotice")}</span>
                          <button
                            type="button"
                            onClick={() => dismissNotice("seasonalityFallback")}
                            aria-label={t("bank:trend.dismissNotice")}
                            className="ml-auto shrink-0 -my-0.5 -mr-1 rounded p-1 text-muted-foreground hover:bg-yellow-500/10 hover:text-foreground"
                            data-testid="dismiss-seasonality-fallback-notice"
                          >
                            <X className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      )}
                    </TooltipProvider>
                  );
                })()}
              </div>
              {monthlyTrendData.length === 1 && (
                <div className="flex items-center gap-2 rounded-md border border-primary/20 bg-primary/5 px-3 py-2 text-xs text-muted-foreground" data-testid="seasonality-single-tip">
                  <Activity className="w-3.5 h-3.5 text-primary shrink-0" />
                  {t("bank:trend.uploadMoreTip")}
                </div>
              )}
              <ResponsiveContainer width="100%" height={200}>
                <LineChart data={monthlyTrendData} margin={{ top: 4, right: 12, left: 0, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" className="stroke-border/30" />
                  <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                  <YAxis tick={{ fontSize: 11 }} tickFormatter={fmtCompact} />
                  <RechartsTooltip
                    formatter={(v: any, name: any) => [fmt$(v), name]}
                    contentStyle={{ fontSize: 12 }}
                  />
                  <Legend formatter={(v) => <span className="text-xs">{v}</span>} />
                  <Line type="monotone" dataKey="revenue" name={t("bank:trend.legendRevenue")} stroke="#10b981" strokeWidth={2.5} dot={{ r: 4 }} activeDot={{ r: 6 }} />
                  <Line type="monotone" dataKey="expenses" name={t("bank:trend.legendExpenses")} stroke="hsl(var(--destructive))" strokeWidth={2.5} dot={{ r: 4 }} activeDot={{ r: 6 }} />
                  <Line type="monotone" dataKey="net" name={t("bank:trend.legendNet")} stroke="hsl(var(--primary))" strokeWidth={2.5} dot={{ r: 4 }} activeDot={{ r: 6 }} strokeDasharray="5 3" />
                </LineChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>

          {/* Cash Position Trend chart hidden — not ready to show */}

          <Card data-testid="chart-health-ratio">
            <CardHeader className="pb-1 pt-3 px-4">
              <CardTitle className="text-sm font-semibold flex items-center gap-2">
                <Activity className="w-4 h-4 text-primary" />
                {t("bank:healthRatio.title")}
              </CardTitle>
              <CardDescription className="text-xs">
                {t("bank:healthRatio.description")}
              </CardDescription>
            </CardHeader>
            <CardContent className="px-4 pb-3">
              {monthlyTrendData.length === 1 && (
                <div className="flex items-center gap-2 rounded-md border border-primary/20 bg-primary/5 px-3 py-2 text-xs text-muted-foreground mb-3" data-testid="health-ratio-single-tip">
                  <Activity className="w-3.5 h-3.5 text-primary shrink-0" />
                  {t("bank:trend.uploadMoreTip")}
                </div>
              )}
              <ResponsiveContainer width="100%" height={180}>
                <LineChart data={monthlyTrendData} margin={{ top: 4, right: 12, left: 0, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" className="stroke-border/30" />
                  <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                  <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => `${v.toFixed(1)}x`} />
                  <RechartsTooltip
                    formatter={(v: any) => [`${Number(v).toFixed(2)}x`, t("bank:healthRatio.tooltipLabel")]}
                    contentStyle={{ fontSize: 12 }}
                  />
                  <ReferenceLine y={1} stroke="#94a3b8" strokeDasharray="4 2" label={{ value: t("bank:healthRatio.breakeven"), position: "insideTopRight", fontSize: 10, fill: "#94a3b8" }} />
                  <Line
                    type="monotone"
                    dataKey="ratio"
                    name={t("bank:healthRatio.tooltipLabel")}
                    stroke="hsl(var(--primary))"
                    strokeWidth={2.5}
                    connectNulls
                    dot={(props: any) => {
                      const { cx, cy, index } = props;
                      const ratio = monthlyTrendData[index].ratio;
                      if (ratio == null) return <g key={`dot-ratio-${index}`} />;
                      const color = ratio >= 1 ? "#10b981" : "hsl(var(--destructive))";
                      return <circle key={`dot-ratio-${index}`} cx={cx} cy={cy} r={5} fill={color} stroke="white" strokeWidth={1.5} />;
                    }}
                    activeDot={{ r: 6 }}
                  />
                </LineChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>

          {hasFullVerified && (categoryChartData.length > 0 || categoryMonthOptions.length > 0) && (
            <Card data-testid="chart-spending-breakdown">
              <CardHeader className="pb-1 pt-3 px-4">
                <div className="flex items-center justify-between gap-2 flex-wrap">
                  <div>
                    <CardTitle className="text-sm font-semibold flex items-center gap-2">
                      <DollarSign className="w-4 h-4 text-primary" />
                      {t("bank:spending.title")}
                    </CardTitle>
                    <CardDescription className="text-xs mt-0.5">
                      {categoryMonth === "total"
                        ? t("bank:spending.descTotal")
                        : t("bank:spending.descMonth", { month: fmtMonth(categoryMonth) })}
                    </CardDescription>
                  </div>
                  <div className="flex items-center gap-2 flex-wrap">
                    {categoryMonthOptions.length > 0 && (
                      <Select
                        value={categoryMonth}
                        onValueChange={(v) => { setCategoryMonth(v); setSelectedCategory(null); }}
                      >
                        <SelectTrigger className="h-7 text-xs w-[110px]" data-testid="select-category-month">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="total">{t("bank:spending.allMonths")}</SelectItem>
                          {categoryMonthOptions.map((m) => (
                            <SelectItem key={m} value={m}>{fmtMonth(m)}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    )}
                    {selectedCategory && (
                      <button
                        type="button"
                        className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground border border-border rounded px-2 py-1 transition-colors"
                        onClick={() => setSelectedCategory(null)}
                        data-testid="clear-category-filter"
                      >
                        <span
                          className="inline-block w-2 h-2 rounded-full shrink-0"
                          style={{ background: CATEGORY_CHART_HEX[selectedCategory] ?? "#9ca3af" }}
                        />
                        {categoryLabel(selectedCategory, t)}
                        <span className="ml-1 text-muted-foreground/60">✕</span>
                      </button>
                    )}
                  </div>
                </div>
              </CardHeader>
              <CardContent className="px-4 pb-3">
                <ResponsiveContainer width="100%" height={Math.max(160, categoryChartData.length * 28)}>
                  <BarChart
                    data={categoryChartData}
                    layout="vertical"
                    margin={{ top: 0, right: 60, left: 4, bottom: 0 }}
                    onClick={(e) => {
                      if (e?.activePayload?.[0]?.payload?.name) {
                        handleCategoryClick(e.activePayload[0].payload.name);
                      }
                    }}
                    style={{ cursor: "pointer" }}
                    data-testid="spending-breakdown-chart"
                  >
                    <CartesianGrid strokeDasharray="3 3" horizontal={false} className="stroke-border/30" />
                    <XAxis type="number" tick={{ fontSize: 10 }} tickFormatter={fmtCompact} />
                    <YAxis
                      type="category"
                      dataKey="name"
                      width={130}
                      tick={{ fontSize: 10 }}
                      tickLine={false}
                    />
                    <RechartsTooltip
                      formatter={(v: any, _: any, props: any) => [fmt$(v), props.payload?.name]}
                      contentStyle={{ fontSize: 12 }}
                      cursor={{ fill: "hsl(var(--muted))" }}
                    />
                    <Bar dataKey="value" radius={[0, 3, 3, 0]} maxBarSize={20} label={{ position: "right", fontSize: 10, formatter: fmtCompact }}>
                      {categoryChartData.map((entry) => (
                        <Cell
                          key={entry.name}
                          fill={entry.color}
                          opacity={selectedCategory && selectedCategory !== entry.name ? 0.3 : 1}
                        />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          )}

          {avgDailyBalanceData.length > 0 && (
            <Card data-testid="chart-avg-daily-balance">
              <CardHeader className="pb-1 pt-3 px-4">
                <CardTitle className="text-sm font-semibold flex items-center gap-2">
                  <DollarSign className="w-4 h-4 text-primary" />
                  {t("bank:avgBalance.title")}
                </CardTitle>
                <CardDescription className="text-xs">
                  {t("bank:avgBalance.description")}
                </CardDescription>
              </CardHeader>
              <CardContent className="px-4 pb-3">
                {avgDailyBalanceData.length === 1 && (
                  <div className="flex items-center gap-2 rounded-md border border-primary/20 bg-primary/5 px-3 py-2 text-xs text-muted-foreground mb-3" data-testid="avg-daily-balance-single-tip">
                    <Activity className="w-3.5 h-3.5 text-primary shrink-0" />
                    {t("bank:trend.uploadMoreTip")}
                  </div>
                )}
                <ResponsiveContainer width="100%" height={180}>
                  <BarChart data={avgDailyBalanceData} margin={{ top: 4, right: 12, left: 0, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" className="stroke-border/30" />
                    <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                    <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => fmtCompact(v)} />
                    <RechartsTooltip
                      content={({ active, payload }) => {
                        if (!active || !payload?.length) return null;
                        const d = payload[0].payload as typeof avgDailyBalanceData[number];
                        return (
                          <div className="rounded-lg border bg-background p-3 shadow-md text-xs space-y-1" data-testid="avg-daily-balance-tooltip">
                            <p className="font-semibold">{d.month}</p>
                            <p>{t("bank:avgBalance.average")} <span className="font-medium">{fmt$(d.avgDailyBalance)}</span></p>
                            {d.daysInPeriod != null && (
                              <p>{t("bank:avgBalance.daysInPeriod")} <span className="font-medium">{d.daysInPeriod}</span></p>
                            )}
                            <p>{t("bank:avgBalance.source")} <span className="font-medium">{d.source}</span></p>
                          </div>
                        );
                      }}
                    />
                    <Bar dataKey="avgDailyBalance" name={t("bank:avgBalance.barName")} radius={[3, 3, 0, 0]} fill="hsl(var(--primary))" />
                  </BarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          )}
        </div>
      )}

      <div className="grid grid-cols-1 gap-4">
        <div className="space-y-4">
          <Card>
            <CardHeader className="pb-2">
              <button
                className="flex items-center gap-2 text-sm font-semibold w-full text-left"
                onClick={() => setExpandedStatements((v) => !v)}
                data-testid="toggle-statements"
              >
                {expandedStatements ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                {t("bank:statements.heading", { count: statements.length })}
              </button>
            </CardHeader>
            {expandedStatements && (
              <CardContent className="pt-0">
                {statementsLoading ? (
                  <div className="space-y-2">
                    <Skeleton className="h-12 w-full" />
                    <Skeleton className="h-12 w-full" />
                  </div>
                ) : statements.length === 0 ? (
                  <p className="text-sm text-muted-foreground text-center py-4">{t("bank:statements.none")}</p>
                ) : (
                  <div className="space-y-2">
                    {statements.map((s) => {
                      const accuracy = parseN(s.accuracyScore);
                      const closing = parseN(s.closingBalance);
                      const credits = effectiveCredits(s);
                      const statementTxns = allTransactions
                        .filter((t) => t.bankStatementId === s.id)
                        .sort((a, b) => (a.transactionDate ?? "").localeCompare(b.transactionDate ?? ""));
                      const txnCount = statementTxns.length;
                      // Total money-out: stated summary (withdrawals + checks + fees)
                      // so checks are never dropped from the displayed figure.
                      const debits = effectiveMoneyOut(s);
                      const txnsExpanded = expandedTxnStatementIds.has(s.id);
                      const readiness = readinessById[s.id];
                      const tier = readiness?.tier ?? "review_required";
                      return (
                        <div
                          key={s.id}
                          className="rounded-lg border text-sm overflow-hidden"
                          data-testid={`statement-row-${s.id}`}
                        >
                          <div className="flex items-center gap-3 p-3">
                            <FileText className="w-4 h-4 text-muted-foreground shrink-0" />
                            <div className="flex-1 min-w-0">
                              <p className="font-medium truncate">{s.fileName}</p>
                              <p className="text-xs text-muted-foreground">
                                {s.accountPeriodStart && s.accountPeriodEnd
                                  ? `${s.accountPeriodStart} → ${s.accountPeriodEnd}`
                                  : t("bank:statements.periodUnknown")}
                                {closing != null ? ` · ${t("bank:statements.closing", { amount: fmt$(closing) })}` : ""}
                                {credits != null ? ` · ${t("bank:statements.credits", { amount: fmt$(credits) })}` : ""}
                                {debits != null ? ` · ${t("bank:statements.debits", { amount: fmt$(debits) })}` : ""}
                              </p>
                              {(() => {
                                const prov = avgBalanceProvenance(s, t);
                                if (!prov) return null;
                                let audit: {
                                  total?: number | null;
                                  days?: number | null;
                                  computedAvg?: number | null;
                                  sourceRows?: unknown[];
                                  anchors?: unknown[];
                                  method?: string;
                                } | null = null;
                                if (s.avgDailyBalanceAuditJson) {
                                  try {
                                    audit = JSON.parse(s.avgDailyBalanceAuditJson);
                                  } catch {
                                    audit = null;
                                  }
                                }
                                const auditExpanded = expandedAvgAuditIds.has(s.id);
                                return (
                                  <div>
                                    <p
                                      className={`text-xs ${prov.verified ? "text-emerald-600 dark:text-emerald-400" : "text-amber-600 dark:text-amber-400"} ${audit ? "cursor-pointer inline-flex items-center gap-1" : ""}`}
                                      data-testid={`text-avg-balance-${s.id}`}
                                      onClick={audit ? () => setExpandedAvgAuditIds((prev) => {
                                        const next = new Set(prev);
                                        if (next.has(s.id)) next.delete(s.id); else next.add(s.id);
                                        return next;
                                      }) : undefined}
                                    >
                                      {audit ? (auditExpanded ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />) : null}
                                      {t("bank:statements.averageBalanceLine", { amount: fmt$(effectiveAvgBalance(s)!), label: prov.label })}
                                    </p>
                                    {audit && auditExpanded ? (
                                      <div
                                        className="text-xs text-muted-foreground mt-1 pl-4 space-y-0.5"
                                        data-testid={`avg-balance-audit-${s.id}`}
                                      >
                                        <p>{t("bank:statements.audit.sumDailyBalances", { amount: audit.total != null ? fmt$(audit.total) : "—" })}</p>
                                        <p>{t("bank:statements.audit.daysInPeriod", { days: audit.days ?? "—" })}</p>
                                        <p>{t("bank:statements.audit.computedAdb", { amount: audit.computedAvg != null ? fmt$(audit.computedAvg) : "—" })}{audit.total != null && audit.days ? t("bank:statements.audit.computedAdbCalc", { total: fmt$(audit.total), days: audit.days }) : ""}</p>
                                        <p>{t("bank:statements.audit.sourceRows", { rows: Array.isArray(audit.sourceRows) ? audit.sourceRows.length : 0, anchors: Array.isArray(audit.anchors) ? audit.anchors.length : 0 })}</p>
                                        <p>{t("bank:statements.audit.method", { method: audit.method ?? "—" })}</p>
                                      </div>
                                    ) : null}
                                  </div>
                                );
                              })()}
                            </div>
                            {txnCount > 0 && tier === "full_verified" ? (
                              <button
                                className="flex items-center gap-1 text-xs shrink-0 text-emerald-600 hover:text-emerald-700 font-medium"
                                onClick={() => setExpandedTxnStatementIds((prev) => {
                                  const next = new Set(prev);
                                  if (txnsExpanded) next.delete(s.id); else next.add(s.id);
                                  return next;
                                })}
                                data-testid={`txn-count-badge-${s.id}`}
                              >
                                {txnsExpanded ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
                                {t("bank:statements.txnsCount", { count: txnCount })}
                              </button>
                            ) : s.transactionExtractionFailed && (
                              <Badge
                                variant="outline"
                                className="text-xs shrink-0 text-amber-600 dark:text-amber-400 border-amber-400 dark:border-amber-600 bg-amber-50 dark:bg-amber-950/30"
                                data-testid={`txn-warning-badge-${s.id}`}
                              >
                                <AlertTriangle className="w-3 h-3 mr-1" />
                                {t("bank:statements.extractionFailed")}
                              </Badge>
                            )}
                            {/* A "review_required" statement gets a single, calm "Needs review"
                                badge instead of a scary red "{accuracy}% accurate" + "Low
                                confidence" stack. The accuracyScore is a coarse confidence TIER
                                (low=40), not a measured accuracy, and the printed totals are
                                usually read correctly — so the harsh percentage misled dealers.
                                The discrepancy details still appear in the panel below the row. */}
                            {s.fallbackPath === "text_chunked" && accuracy != null && accuracy > 40 && tier !== "review_required" && (
                              <TooltipProvider>
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <Badge
                                      variant="outline"
                                      className="text-xs shrink-0 text-muted-foreground border-border bg-muted/40 cursor-default"
                                      data-testid={`text-extracted-badge-${s.id}`}
                                    >
                                      <FileText className="w-3 h-3 mr-1" />
                                      {t("bank:statements.textExtracted")}
                                    </Badge>
                                  </TooltipTrigger>
                                  <TooltipContent side="top" className="max-w-[260px] text-xs">
                                    {t("bank:statements.textExtractedTip")}
                                  </TooltipContent>
                                </Tooltip>
                              </TooltipProvider>
                            )}
                            {tier === "review_required" && (
                              <TooltipProvider>
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <Badge
                                      variant="outline"
                                      className="text-xs shrink-0 text-amber-600 dark:text-amber-400 border-amber-400 dark:border-amber-600 bg-amber-50 dark:bg-amber-950/30 cursor-default"
                                      data-testid={`review-required-badge-${s.id}`}
                                    >
                                      <AlertTriangle className="w-3 h-3 mr-1" />
                                      {t("bank:statements.needsReview")}
                                    </Badge>
                                  </TooltipTrigger>
                                  <TooltipContent side="top" className="max-w-[280px] text-xs">
                                    {t("bank:statements.needsReviewTip")}
                                  </TooltipContent>
                                </Tooltip>
                              </TooltipProvider>
                            )}
                            {tier === "summary_verified_limited_transactions" && (
                              <Badge
                                variant="outline"
                                className="text-xs shrink-0 text-sky-700 dark:text-sky-300 border-sky-300 dark:border-sky-800 bg-sky-50 dark:bg-sky-950/30"
                                data-testid={`verified-summary-badge-${s.id}`}
                              >
                                <CheckCircle2 className="w-3 h-3 mr-1" />
                                {t("bank:statements.verifiedSummary")}
                              </Badge>
                            )}
                            {tier !== "full_verified" && s.filePath && (
                              <Button
                                variant="ghost"
                                size="icon"
                                className="shrink-0 h-7 w-7"
                                disabled={reExtractMutation.isPending}
                                onClick={() => reExtractMutation.mutate(s.id)}
                                title={t("bank:statements.reExtractTitle")}
                                data-testid={`re-extract-btn-${s.id}`}
                              >
                                {reExtractMutation.isPending && reExtractMutation.variables === s.id ? (
                                  <RefreshCw className="w-3.5 h-3.5 text-amber-500 animate-spin" />
                                ) : (
                                  <RefreshCw className="w-3.5 h-3.5 text-amber-500" />
                                )}
                              </Button>
                            )}
                            {s.filePath ? (
                              <Button
                                variant="ghost"
                                size="icon"
                                className="shrink-0 h-7 w-7"
                                onClick={() => window.open(`/api/bank-statements/${s.id}/download`, "_blank")}
                                title={t("bank:statements.viewOriginalTitle")}
                                data-testid={`view-statement-${s.id}`}
                              >
                                <Eye className="w-3.5 h-3.5 text-muted-foreground" />
                              </Button>
                            ) : null}
                            <Button
                              variant="ghost"
                              size="icon"
                              className="shrink-0 h-7 w-7"
                              onClick={() => setPendingDeleteId(s.id)}
                              data-testid={`delete-statement-${s.id}`}
                            >
                              <Trash2 className="w-3.5 h-3.5 text-muted-foreground" />
                            </Button>
                          </div>
                          {tier === "review_required" && (
                            <div
                              className="flex items-start gap-2 px-3 py-2 border-t bg-muted/40 text-xs text-muted-foreground"
                              data-testid={`review-message-${s.id}`}
                            >
                              <Info className="w-3.5 h-3.5 shrink-0 mt-0.5" />
                              <span>{t("bank:statements.reviewMessage")}</span>
                            </div>
                          )}
                          {tier === "review_required" && !allReviewRequired && (
                            <div className="px-3 py-2 border-t">
                              <ReviewSummaryEditor
                                statement={s}
                                isSaving={verifySummaryMutation.isPending && verifySummaryMutation.variables?.id === s.id}
                                onSave={(values) => verifySummaryMutation.mutate({ id: s.id, values })}
                              />
                            </div>
                          )}
                          {txnsExpanded && txnCount > 0 && tier === "full_verified" && (
                            <div className="border-t bg-muted/30" data-testid={`txn-list-${s.id}`}>
                              {/* Categorization summary */}
                              {(() => {
                                const debitTxns = statementTxns.filter((t) => t.type === "debit");
                                const uncategorized = debitTxns.filter((t) => (t.category === "Other" || !t.category) && !t.manuallyCorrected);
                                const pct = debitTxns.length > 0
                                  ? Math.round(((debitTxns.length - uncategorized.length) / debitTxns.length) * 100)
                                  : 100;
                                if (debitTxns.length === 0) return null;
                                return (
                                  <div className="px-3 py-1.5 border-b flex items-center justify-between bg-background/60 text-xs" data-testid={`txn-categorization-summary-${s.id}`}>
                                    <span>
                                      <span className="font-medium">{pct}%</span>
                                      <span className="text-muted-foreground">{t("bank:statements.autoCategorized")}</span>
                                      {uncategorized.length > 0 && (
                                        <>
                                          {" · "}
                                          <span className="text-amber-600 dark:text-amber-400 font-medium">{uncategorized.length}</span>
                                          <span className="text-muted-foreground">{t("bank:statements.needReview", { count: uncategorized.length })}</span>
                                        </>
                                      )}
                                    </span>
                                    <span className="text-muted-foreground">{t("bank:statements.debitsCount", { count: debitTxns.length })}</span>
                                  </div>
                                );
                              })()}
                              <div className="max-h-72 overflow-y-auto">
                                <table className="w-full text-xs">
                                  <thead className="sticky top-0 bg-muted/80 backdrop-blur-sm">
                                    <tr className="text-left text-muted-foreground">
                                      <th className="px-3 py-1.5 font-medium w-24">{t("bank:statements.colDate")}</th>
                                      <th className="px-3 py-1.5 font-medium">{t("bank:statements.colPayee")}</th>
                                      <th className="px-3 py-1.5 font-medium">{t("bank:statements.colCategory")}</th>
                                      <th className="px-3 py-1.5 font-medium text-right w-24">{t("bank:statements.colAmount")}</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {statementTxns.map((txn) => {
                                      const amt = parseN(txn.amount);
                                      const isCredit = txn.type === "credit";
                                      const isEditing = editingCategoryTxnId === txn.id;
                                      const isSaving = updateCategoryMutation.isPending && updateCategoryMutation.variables?.txnId === txn.id;
                                      return (
                                        <tr
                                          key={txn.id}
                                          className={`border-t border-border/50 hover:bg-muted/50 transition-colors ${
                                            selectedCategory && (txn.category ?? "Other") === selectedCategory
                                              ? "bg-primary/8 ring-1 ring-inset ring-primary/20"
                                              : selectedCategory
                                              ? "opacity-40"
                                              : ""
                                          }`}
                                          data-testid={`txn-row-${txn.id}`}
                                        >
                                          <td className="px-3 py-1.5 text-muted-foreground whitespace-nowrap">{txn.transactionDate ?? "—"}</td>
                                          <td className="px-3 py-1.5 max-w-0 truncate">
                                            <span className="block truncate" title={txn.payee ?? txn.description ?? ""}>{txn.payee || txn.description || "—"}</span>
                                            {txn.includedInStatementTotals === false && (
                                              <span className="mt-0.5 flex items-center gap-1.5" data-testid={`txn-returned-${txn.id}`}>
                                                <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-medium bg-amber-100 text-amber-800 dark:bg-amber-950/40 dark:text-amber-300">
                                                  <AlertTriangle className="w-2.5 h-2.5 shrink-0" />
                                                  {t("bank:statements.returnedRejected")}
                                                </span>
                                                <span className="text-[10px] text-muted-foreground truncate" title={txn.exclusionReason ?? t("bank:statements.excludedFromTotals")}>
                                                  {t("bank:statements.excludedFromTotals")}
                                                </span>
                                              </span>
                                            )}
                                          </td>
                                          <td className="px-3 py-1 whitespace-nowrap">
                                            {isEditing ? (
                                              <select
                                                autoFocus
                                                defaultValue={txn.category ?? "Other"}
                                                disabled={isSaving}
                                                className="text-xs border border-border rounded px-1 py-0.5 bg-background text-foreground focus:outline-none focus:ring-1 focus:ring-ring"
                                                data-testid={`category-select-${txn.id}`}
                                                onChange={(e) => {
                                                  updateCategoryMutation.mutate({
                                                    txnId: txn.id,
                                                    category: e.target.value,
                                                    payee: txn.payee,
                                                  });
                                                }}
                                                onBlur={() => {
                                                  if (!isSaving) setEditingCategoryTxnId(null);
                                                }}
                                              >
                                                {TRANSACTION_CATEGORY_OPTIONS.map((cat) => (
                                                  <option key={cat} value={cat}>{categoryLabel(cat, t)}</option>
                                                ))}
                                              </select>
                                            ) : (
                                              <button
                                                type="button"
                                                className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-xs font-medium group transition-opacity ${categoryColor(txn.category)}`}
                                                onClick={() => setEditingCategoryTxnId(txn.id)}
                                                title={t("bank:statements.clickToChangeCategory")}
                                                data-testid={`category-badge-${txn.id}`}
                                              >
                                                {categoryLabel(txn.category ?? "Other", t)}
                                                <Pencil className="w-2.5 h-2.5 opacity-0 group-hover:opacity-60 transition-opacity shrink-0" />
                                              </button>
                                            )}
                                          </td>
                                          <td className={`px-3 py-1.5 text-right font-medium whitespace-nowrap ${isCredit ? "text-emerald-600" : "text-rose-600"}`}>
                                            {isCredit ? "+" : "-"}{amt != null ? fmt$(Math.abs(amt)) : "—"}
                                          </td>
                                        </tr>
                                      );
                                    })}
                                  </tbody>
                                </table>
                              </div>
                            </div>
                          )}
                          {txnCount === 0 && s.transactionExtractionFailed && (
                            <div
                              className="px-3 pb-2.5 flex items-start gap-2 text-xs text-amber-700 dark:text-amber-400 bg-amber-50 dark:bg-amber-950/30 border-t border-amber-200 dark:border-amber-800"
                              data-testid={`txn-extraction-warning-${s.id}`}
                            >
                              <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                              <span className="flex-1">
                                {t("bank:statements.extractionWarning")}
                              </span>
                              <Button
                                variant="outline"
                                size="sm"
                                className="shrink-0 h-6 text-xs px-2 border-amber-300 dark:border-amber-700 text-amber-700 dark:text-amber-400 hover:bg-amber-100 dark:hover:bg-amber-900/30"
                                disabled={reExtractMutation.isPending}
                                onClick={() => reExtractMutation.mutate(s.id)}
                                data-testid={`re-extract-btn-${s.id}`}
                              >
                                {reExtractMutation.isPending && reExtractMutation.variables === s.id ? (
                                  <RefreshCw className="w-3 h-3 mr-1 animate-spin" />
                                ) : (
                                  <RefreshCw className="w-3 h-3 mr-1" />
                                )}
                                {t("bank:statements.reExtract")}
                              </Button>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </CardContent>
            )}
          </Card>
        </div>
      </div>

      <AlertDialog open={pendingDeleteId != null} onOpenChange={(v) => { if (!v) setPendingDeleteId(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t("bank:deleteDialog.title")}</AlertDialogTitle>
            <AlertDialogDescription>
              {t("bank:deleteDialog.description")}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t("common:cancel")}</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                if (pendingDeleteId) {
                  deleteStatementMutation.mutate(pendingDeleteId);
                  setPendingDeleteId(null);
                }
              }}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {t("common:delete")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={showExportConfirm} onOpenChange={setShowExportConfirm}>
        <AlertDialogContent data-testid="dialog-export-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle>{t("bank:exportDialog.title")}</AlertDialogTitle>
            <AlertDialogDescription>
              {dateRange === "custom" ? (
                <Trans
                  i18nKey="bank:exportDialog.customBody"
                  values={{
                    range: customMode === "month"
                      ? formatMonthYear(customFrom)
                      : `${formatMonthYear(customFrom)} – ${formatMonthYear(customTo)}`,
                    mode: customMode === "month" ? t("bank:exportDialog.specificMonth") : t("bank:exportDialog.customRange"),
                  }}
                  components={{ strong: <strong /> }}
                />
              ) : (
                <Trans
                  i18nKey="bank:exportDialog.rangeBody"
                  values={{
                    span: formatDateSpan(dateRange as Exclude<DateRangeOption, "custom">, t),
                    label: dateRangeOptions(t).find((o) => o.value === dateRange)?.pdfLabel,
                  }}
                  components={{ strong: <strong /> }}
                />
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="button-export-cancel">{t("common:cancel")}</AlertDialogCancel>
            <AlertDialogAction
              data-testid="button-export-confirm"
              onClick={() => {
                setShowExportConfirm(false);
                handleExportPDF();
              }}
            >
              {t("bank:exportDialog.downloadPdf")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
